import Phaser from 'phaser';
import { store } from '../state/store';
import { saveGame } from '../state/save';
import { runDayRollover } from '../state/dayCycle';
import {
  addGameMinutes,
  isPastForceSleep,
  minutesSinceDayStart,
  realMsToGameMinutes,
} from '../sim/time';
import {
  newPlantedCrop,
  growthStage,
  isHarvestable,
  waterCrop,
  harvestCrop,
  type PlantedCrop,
} from '../sim/crops';
import { seasonOf, isFestivalDay, festivalName, yearOf } from '../sim/seasons';
import { getCrop, CROPS, cropInSeason } from '../data/crops';
import { getItem } from '../data/items';
import { getDecoration, type DecorationDef } from '../data/decorations';
import { CONFIG, type EnergyAction } from '../data/config';
import { personalization } from '../data/personalization';
import { TX, TILESET_KEY, TILESET_NAME, TILE_INDEX, COLLIDING_TILE_INDICES, cropTextureKey } from '../registry/keys';
import { findPath, adjacentWalkable } from '../world/pathfinding';
import { celebrate, confetti } from '../world/celebrate';
import { trailSign } from '../world/trailSign';
import {
  MAP_WIDTH,
  MAP_HEIGHT,
  HOUSE,
  SHOP,
  SIGN,
  PLAYER_SPAWN,
  PET_SPAWN,
  CLARA_HOME,
  COOP,
  COOP_RADIUS,
  MAILBOX,
  TRAIL,
} from '../world/farmLayout';
import type { ToolId } from '../data/tools';
import type { Animal, PlacedDecoration } from '../state/gameState';
import { playSfx, setSeason, setRain, setFestival } from '../audio/sfx';
import { getHat, hatTexture, getClaraOutfit, claraOutfitTexture, getCollar, collarColor } from '../data/hats';
import { HOUSE_TIERS } from '../data/house';
import { AUDIO, type AudioKey } from '../registry/keys';
import { tileKey, type TileXY } from '../types';
import type { UIScene } from './UIScene';

const TS = CONFIG.tileSize;
const SOIL_DRY = TILE_INDEX.soilDry + 1;
const SOIL_WET = TILE_INDEX.soilWet + 1;
const TREE_GID = TILE_INDEX.tree + 1;
const FLOWER_GID = TILE_INDEX.flowers + 1;
const WATER_GID = TILE_INDEX.water + 1;
const GRASS_GID = TILE_INDEX.grass + 1;
const GRASSALT_GID = TILE_INDEX.grassAlt + 1;
const FONT = 'monospace';
// Real character art is a 48×48 frame with the figure in the upper-middle; this
// puts the feet at the tile. Tweak if the character floats/sinks visually.
const PLAYER_ORIGIN_Y = 0.7;
const HAT_OFFSET_Y = 16; // hat sits this many px above the player origin (tune to taste)
const SEASON_SUN: Record<string, number> = { spring: 0.8, summer: 1, fall: 0.5, winter: 0.18 };
declare const __BUILD_ID__: string; // injected at build (git describe)

export class WorldScene extends Phaser.Scene {
  private map!: Phaser.Tilemaps.Tilemap;
  private groundLayer!: Phaser.Tilemaps.TilemapLayer;
  private objectsLayer!: Phaser.Tilemaps.TilemapLayer;
  private soilLayer!: Phaser.Tilemaps.TilemapLayer;

  private player!: Phaser.GameObjects.Sprite;
  private pet!: Phaser.GameObjects.Image;
  private clara!: Phaser.GameObjects.Image;
  private moveMarker!: Phaser.GameObjects.Image;
  private nightOverlay!: Phaser.GameObjects.Rectangle;
  private seasonOverlay!: Phaser.GameObjects.Rectangle;
  private rays: Phaser.GameObjects.Image[] = [];
  private fireflies: Phaser.GameObjects.Image[] = [];
  private moon?: Phaser.GameObjects.Image;
  private hat?: Phaser.GameObjects.Image;
  private claraHat?: Phaser.GameObjects.Image;
  private petCollar?: Phaser.GameObjects.Rectangle;
  private mailFlag?: Phaser.GameObjects.Image;
  private buildingSprites = new Map<string, Phaser.GameObjects.Image>();
  private movingBuilding: string | null = null;
  private readonly BUILDINGS: { id: string; w: number; h: number; tex: string; def: TileXY }[] = [
    { id: 'house', w: HOUSE.wTiles, h: HOUSE.hTiles, tex: HOUSE.texture, def: { x: HOUSE.tileX, y: HOUSE.tileY } },
    { id: 'shop', w: SHOP.wTiles, h: SHOP.hTiles, tex: SHOP.texture, def: { x: SHOP.x, y: SHOP.y } },
    { id: 'sign', w: 1, h: 1, tex: SIGN.texture, def: { x: SIGN.x, y: SIGN.y } },
    { id: 'mailbox', w: 1, h: 1, tex: TX.mailbox, def: { x: MAILBOX.x, y: MAILBOX.y } },
    { id: 'barn', w: 2, h: 2, tex: TX.barn, def: { x: 1, y: 4 } },
    { id: 'trail', w: 1, h: 1, tex: TX.trailArch, def: { x: TRAIL.x, y: TRAIL.y } },
  ];

  private cropSprites = new Map<string, Phaser.GameObjects.Image>();
  private weedSprites = new Map<string, Phaser.GameObjects.Image>();
  private decoSprites: Phaser.GameObjects.Image[] = [];
  private waterTiles: TileXY[] = [];
  private animalSprites = new Map<string, Phaser.GameObjects.Sprite>();
  private eggSprites = new Map<string, Phaser.GameObjects.Image>();
  private animalWander = new Map<string, { target: TileXY | null; wait: number }>();
  private fishing: {
    bobber: Phaser.GameObjects.Image;
    water: TileXY;
    biting: boolean;
    biteEvent?: Phaser.Time.TimerEvent;
    missEvent?: Phaser.Time.TimerEvent;
  } | null = null;
  private pinching = false;
  private pinchStartDist: number | null = null;
  private pinchStartZoom = 1;
  private blocked = new Set<string>();
  private mapBlocked = new Set<string>();

  private playerTile: TileXY = { ...PLAYER_SPAWN };
  private path: TileXY[] = [];
  private pendingArrival: (() => void) | null = null;
  private lastFacing: 'down' | 'up' | 'side' = 'down';
  private dragArmed = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragMoved = false;
  private petMode: 'follow' | 'wander' | 'clara' | 'chase' | 'sleep' = 'follow';
  private petTarget: { x: number; y: number } | null = null;
  private petChase: Phaser.GameObjects.Sprite | null = null;
  private petCircling = false;
  private claraCraving: string | null = null;
  private claraThought?: Phaser.GameObjects.Image;

  private minuteAcc = 0;
  private sleeping = false;
  private lastSeasonId = '';

  private placementDeco: string | null = null;
  private ghost: Phaser.GameObjects.Image | null = null;
  private teleportMode = false;

  private claraTile: TileXY = { ...CLARA_HOME };
  private claraTarget: TileXY | null = null;
  private claraWait = 0;

  private cornerTaps = 0;
  private cornerTapAt = 0;

  private get ui(): UIScene {
    return this.scene.get('UI') as UIScene;
  }

  constructor() {
    super('World');
  }

  create(): void {
    this.buildMap();
    this.buildStructures();
    this.buildEntities();
    this.setupPlayerAnims();

    this.moveMarker = this.add.image(0, 0, TX.moveMarker).setDepth(3).setAlpha(0);
    this.nightOverlay = this.add
      .rectangle(0, 0, 10, 10, 0x223055, 0)
      .setOrigin(0, 0)
      .setDepth(9000)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.seasonOverlay = this.add
      .rectangle(0, 0, 10, 10, 0xffffff, 0)
      .setOrigin(0, 0)
      .setDepth(8900)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);

    this.playerTile = { ...store.get().player };
    this.placePlayerAtTile(this.playerTile);
    this.claraTile = { ...CLARA_HOME };

    this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
    this.cameras.main.setZoom(this.computeZoom());
    this.cameras.main.setRoundPixels(true);
    this.cameras.main.startFollow(this.player, true, 0.15, 0.15);

    this.renderWorldFromState();
    this.setupAmbient();
    this.time.delayedCall(60, () => {
      this.applyWeather();
      const day = store.get().clock.day;
      this.ui.setFestive(isFestivalDay(day));
      setFestival(isFestivalDay(day));
      if (isFestivalDay(day)) {
        this.ui.showToast(`🎪 ${festivalName(day)} today — tap the sign for a prize!`, 'good');
        confetti(this);
      }
      this.checkBirthday();
      this.checkInGameBirthday();
      this.checkFarmAnniversary();
      this.checkV1Launch();
    }); // after UI is ready

    this.input.addPointer(2); // enable multi-touch for pinch-zoom
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.installDevKeys();
    this.scale.on('resize', () => this.cameras.main.setZoom(this.computeZoom()));
  }

  // ---- build ------------------------------------------------------------------

  private buildMap(): void {
    this.map = this.make.tilemap({ key: 'farm' });
    const tileset = this.map.addTilesetImage(TILESET_NAME, TILESET_KEY, TS, TS, 0, 0);
    if (!tileset) throw new Error('tileset failed to load');

    const ground = this.map.createLayer('ground', tileset, 0, 0);
    const objects = this.map.createLayer('objects', tileset, 0, 0);
    const soil = this.map.createBlankLayer('soil', tileset, 0, 0);
    if (!ground || !objects || !soil) throw new Error('map layers failed to create');

    ground.setDepth(0);
    soil.setDepth(1);
    objects.setDepth(2);
    this.groundLayer = ground;
    objects.setCollisionByProperty({ collides: true }); // also flag for any future physics use

    // Deterministic collision: mark tiles whose gid is a known colliding tile.
    const collidingGids = new Set(COLLIDING_TILE_INDICES.map((i) => i + 1));
    objects.forEachTile((t) => {
      if (collidingGids.has(t.index)) this.mapBlocked.add(tileKey(t.x, t.y));
      if (t.index === WATER_GID) this.waterTiles.push({ x: t.x, y: t.y });
    });

    // re-hide previously chopped trees and free those tiles
    for (const t of store.get().chopped) {
      objects.removeTileAt(t.x, t.y);
      this.mapBlocked.delete(tileKey(t.x, t.y));
    }

    this.objectsLayer = objects;
    this.soilLayer = soil;
  }

  private buildStructures(): void {
    for (const b of this.BUILDINGS) {
      const p = this.bPos(b.id);
      const img = this.add.image(p.x * TS, p.y * TS, b.tex).setOrigin(0, 0).setDepth((p.y + b.h) * TS);
      this.buildingSprites.set(b.id, img);
    }
    this.positionHouse();
    trailSign(this, this.bPos('trail').x, this.bPos('trail').y - 1, '🌲 Forest');
    const mp = this.bPos('mailbox');
    this.mailFlag = this.add
      .image(mp.x * TS + 12, mp.y * TS + 2, TX.mailFlag)
      .setOrigin(0, 0)
      .setDepth((mp.y + 2) * TS)
      .setVisible(store.get().mail !== null);
  }

  // ---- building positions (build mode; default = the authored layout) ---------

  private bDef(id: string): { id: string; w: number; h: number; tex: string; def: TileXY } {
    return this.BUILDINGS.find((b) => b.id === id)!;
  }
  private bPos(id: string): TileXY {
    return store.get().buildings[id] ?? this.bDef(id).def;
  }
  private houseDoor(): TileXY {
    const p = this.bPos('house');
    return { x: p.x + 1, y: p.y + this.bDef('house').h - 1 };
  }
  private shopFront(): TileXY {
    const p = this.bPos('shop');
    return { x: p.x + 1, y: p.y + this.bDef('shop').h - 1 };
  }
  private structureTiles(): TileXY[] {
    const out: TileXY[] = [];
    for (const b of this.BUILDINGS) {
      const p = this.bPos(b.id);
      for (let dx = 0; dx < b.w; dx++) for (let dy = 0; dy < b.h; dy++) out.push({ x: p.x + dx, y: p.y + dy });
    }
    return out;
  }

  private buildEntities(): void {
    this.player = this.add.sprite(0, 0, TX.player, 0).setOrigin(0.5, PLAYER_ORIGIN_Y);
    this.pet = this.add.image(0, 0, this.petTexture()).setOrigin(0.5, 1);
    this.clara = this.add.image(0, 0, TX.clara).setOrigin(0.5, 1);
    this.setFoot(this.pet, PET_SPAWN.x, PET_SPAWN.y);
    this.setFoot(this.clara, CLARA_HOME.x, CLARA_HOME.y);
    this.applyHat();
    this.applyClaraOutfit();
    this.applyPetCollar();
  }

  /** Walk cycle from the real character sheet (frames 0–3). Skipped for the
   *  single-frame placeholder. Direction is handled by flipX. */
  private setupPlayerAnims(): void {
    if (this.textures.get(TX.player).frameTotal >= 4 && !this.anims.exists('player-walk')) {
      this.anims.create({
        key: 'player-walk',
        frames: this.anims.generateFrameNumbers(TX.player, { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    // directional walk from the real sheet: row 0 = down, row 1 = up, row 2 = side (faces right)
    if (this.textures.get(TX.player).frameTotal >= 12) {
      if (!this.anims.exists('walk-down')) this.anims.create({ key: 'walk-down', frames: this.anims.generateFrameNumbers(TX.player, { start: 0, end: 3 }), frameRate: 8, repeat: -1 });
      if (!this.anims.exists('walk-up')) this.anims.create({ key: 'walk-up', frames: this.anims.generateFrameNumbers(TX.player, { start: 4, end: 7 }), frameRate: 8, repeat: -1 });
      if (!this.anims.exists('walk-side')) this.anims.create({ key: 'walk-side', frames: this.anims.generateFrameNumbers(TX.player, { start: 8, end: 11 }), frameRate: 8, repeat: -1 });
    }
    // real tool-action frames from the Sprout Lands actions sheet (swing = hoe/axe, water = watering can)
    if (this.textures.get(TX.playerAction).frameTotal >= 18) {
      if (!this.anims.exists('act-swing')) {
        this.anims.create({ key: 'act-swing', frames: this.anims.generateFrameNumbers(TX.playerAction, { frames: [0, 1] }), frameRate: 7, repeat: 0 });
      }
      if (!this.anims.exists('act-water')) {
        this.anims.create({ key: 'act-water', frames: this.anims.generateFrameNumbers(TX.playerAction, { frames: [16, 17] }), frameRate: 6, repeat: 1 });
      }
    }
    if (!this.anims.exists('bird-fly')) {
      this.anims.create({
        key: 'bird-fly',
        frames: this.anims.generateFrameNumbers(TX.bird, { start: 0, end: 1 }),
        frameRate: 7,
        repeat: -1,
      });
    }
  }

  // ---- rendering from state ---------------------------------------------------

  private renderWorldFromState(): void {
    const s = store.get();

    // soil
    this.soilLayer.forEachTile((t) => {
      if (t.index !== -1) this.soilLayer.putTileAt(-1, t.x, t.y);
    });
    for (const tile of s.tilled) {
      const wet = this.cropAt(tile.x, tile.y)?.watered ?? false;
      this.soilLayer.putTileAt(wet ? SOIL_WET : SOIL_DRY, tile.x, tile.y);
    }

    // crops
    for (const sprite of this.cropSprites.values()) sprite.destroy();
    this.cropSprites.clear();
    for (const crop of s.crops) this.addOrUpdateCropSprite(crop);

    // decorations
    for (const d of this.decoSprites) d.destroy();
    this.decoSprites = [];
    for (const placed of s.decorations) {
      const def = getDecoration(placed.decoId);
      if (!def) continue;
      const img = this.add.image(placed.x * TS, placed.y * TS, this.decoTexture(def, placed)).setOrigin(0, 0);
      img.setDepth(def.ground ? 1 : (placed.y + def.h) * TS);
      this.decoSprites.push(img);
    }

    // weeds
    for (const sprite of this.weedSprites.values()) sprite.destroy();
    this.weedSprites.clear();
    for (const wd of s.weeds) this.addWeedSprite(wd);

    // animals + eggs
    for (const sp of this.animalSprites.values()) sp.destroy();
    this.animalSprites.clear();
    this.animalWander.clear();
    for (const a of s.animals) this.addAnimalSprite(a);
    for (const sp of this.eggSprites.values()) sp.destroy();
    this.eggSprites.clear();
    for (const e of s.eggs) this.addEggSprite(e);

    this.rebuildBlocked();
  }

  private addOrUpdateCropSprite(crop: PlantedCrop): void {
    const def = getCrop(crop.cropId);
    if (!def) return;
    const key = cropTextureKey(crop.cropId, growthStage(crop, def));
    const id = tileKey(crop.x, crop.y);
    let sprite = this.cropSprites.get(id);
    if (!sprite) {
      sprite = this.add.image(crop.x * TS + TS / 2, crop.y * TS + TS, key).setOrigin(0.5, 1);
      sprite.setDepth(crop.y * TS + TS);
      this.cropSprites.set(id, sprite);
    } else {
      sprite.setTexture(key);
    }
  }

  private rebuildBlocked(): void {
    this.blocked.clear();
    for (const t of this.structureTiles()) this.blocked.add(tileKey(t.x, t.y));
    for (const placed of store.get().decorations) {
      const def = getDecoration(placed.decoId);
      if (!def || !def.solid) continue;
      for (let dx = 0; dx < def.w; dx++)
        for (let dy = 0; dy < def.h; dy++) this.blocked.add(tileKey(placed.x + dx, placed.y + dy));
    }
    // bridges make their tile walkable again (cross a river)
    for (const placed of store.get().decorations) {
      if (placed.decoId === 'bridge') this.blocked.delete(tileKey(placed.x, placed.y));
    }
  }

  private hasWaterDeco(x: number, y: number): boolean {
    return store.get().decorations.some((d) => d.decoId === 'water_tile' && d.x === x && d.y === y);
  }

  /** Texture for a placed decoration — staged for growables, else its base. */
  private decoTexture(def: DecorationDef, placed: PlacedDecoration): string {
    if (!def.grows) return def.texture;
    const g = placed.growth ?? 0;
    const stage =
      g >= def.grows.days
        ? def.grows.stages - 1
        : Math.min(def.grows.stages - 1, Math.floor((g / def.grows.days) * def.grows.stages));
    return `${def.texture}_${stage}`;
  }

  // ---- helpers ----------------------------------------------------------------

  private setFoot(img: Phaser.GameObjects.Image, tx: number, ty: number): void {
    img.setPosition(tx * TS + TS / 2, ty * TS + TS);
  }
  private placePlayerAtTile(t: TileXY): void {
    this.setFoot(this.player, t.x, t.y);
  }
  private cropAt(x: number, y: number): PlantedCrop | undefined {
    return store.get().crops.find((c) => c.x === x && c.y === y);
  }
  private tilledAt(x: number, y: number): boolean {
    return store.get().tilled.some((t) => t.x === x && t.y === y);
  }

  private isGrassTile(x: number, y: number): boolean {
    const t = this.groundLayer.getTileAt(x, y);
    return !!t && (t.index === GRASS_GID || t.index === GRASSALT_GID);
  }

  /** Any open grass is farmable — not water/paths/structures/objects/crops/weeds/eggs. */
  private canTill(tile: TileXY): boolean {
    return (
      this.isGrassTile(tile.x, tile.y) &&
      !this.isBlocked(tile.x, tile.y) &&
      !this.tilledAt(tile.x, tile.y) &&
      !this.cropAt(tile.x, tile.y) &&
      !this.weedAt(tile) &&
      !this.eggAt(tile) &&
      !this.flowerAt(tile.x, tile.y)
    );
  }

  private isBlocked = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= MAP_WIDTH || y >= MAP_HEIGHT) return true;
    if (this.blocked.has(tileKey(x, y))) return true;
    if (this.mapBlocked.has(tileKey(x, y))) return true;
    return false;
  };

  private computeZoom(): number {
    const short = Math.min(this.scale.width, this.scale.height);
    const z = Math.round(short / (CONFIG.targetTilesAcross * TS));
    return Phaser.Math.Clamp(z, CONFIG.minZoom, CONFIG.maxZoom);
  }

  // ---- input ------------------------------------------------------------------

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    // two fingers down → pinch-zoom gesture (handled in move), not a tap
    if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
      this.path = [];
      this.pendingArrival = null;
      return;
    }
    // top-left corner 5-tap → debug (touch-accessible, before any UI consume)
    if (pointer.x < CONFIG.debugCornerSizePx && pointer.y < CONFIG.debugCornerSizePx) {
      this.registerCornerTap();
      return;
    }
    if (this.scene.isActive('Debug')) return; // debug panel handles its own taps
    if (this.fishing) {
      this.fishingTap();
      return;
    }
    if (this.ui.consumesPointer(pointer.x, pointer.y)) return;

    const tile = { x: Math.floor(pointer.worldX / TS), y: Math.floor(pointer.worldY / TS) };
    if (tile.x < 0 || tile.y < 0 || tile.x >= MAP_WIDTH || tile.y >= MAP_HEIGHT) return;
    this.tapRipple(pointer.worldX, pointer.worldY);

    // arm a possible drag-to-move (single finger, free-walk context)
    this.dragArmed = this.ui.selectedToolId === 'auto' && !this.placementDeco && !this.teleportMode;
    this.dragStartX = pointer.x;
    this.dragStartY = pointer.y;
    this.dragMoved = false;

    if (this.teleportMode) {
      this.teleportMode = false;
      if (!this.isBlocked(tile.x, tile.y)) {
        this.path = [];
        this.playerTile = { ...tile };
        this.placePlayerAtTile(tile);
        store.update((s) => (s.player = { ...tile }));
      }
      return;
    }

    if (this.placementDeco) {
      this.tryPlace(tile);
      return;
    }

    const tool = this.ui.selectedToolId;
    if (tool !== 'auto') {
      this.useTool(tool, tile);
      return;
    }

    this.handleTap(tile);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    const p1 = this.input.pointer1;
    const p2 = this.input.pointer2;
    if (p1.isDown && p2.isDown) {
      const dist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y);
      if (this.pinchStartDist == null) {
        this.pinchStartDist = dist;
        this.pinchStartZoom = this.cameras.main.zoom;
      } else if (this.pinchStartDist > 0) {
        const z = Phaser.Math.Clamp(this.pinchStartZoom * (dist / this.pinchStartDist), CONFIG.minZoom, CONFIG.maxZoom);
        this.cameras.main.setZoom(z);
      }
      this.pinching = true;
      return;
    }
    if (this.placementDeco && this.ghost) {
      this.ghost.setPosition(Math.floor(pointer.worldX / TS) * TS, Math.floor(pointer.worldY / TS) * TS);
      return;
    }
    // once a finger has dragged past the threshold, the update loop walks the player toward it (joystick feel)
    if (this.dragArmed && pointer.isDown && !this.dragMoved) {
      if (Phaser.Math.Distance.Between(pointer.x, pointer.y, this.dragStartX, this.dragStartY) > 14) {
        this.dragMoved = true;
        this.path = []; // abandon any tap-path; the joystick takes over
        this.pendingArrival = null;
      }
    }
  }

  /** Joystick-style continuous walk toward the held finger (its on-screen direction), sliding along walls. */
  private directMove(delta: number): void {
    const p = this.input.activePointer;
    const cam = this.cameras.main;
    const dx = p.x - (this.player.x - cam.worldView.x) * cam.zoom;
    const dy = p.y - (this.player.y - cam.worldView.y) * cam.zoom;
    const d = Math.hypot(dx, dy);
    if (d < 10) {
      this.faceIdle();
      return; // deadzone — finger right on the player
    }
    this.faceMove(dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    const foot = (fx: number, fy: number): TileXY => ({ x: Math.floor(fx / TS), y: Math.floor((fy - 1) / TS) });
    let nx = this.player.x + (dx / d) * step;
    let ny = this.player.y + (dy / d) * step;
    const tX = foot(nx, this.player.y);
    if (nx < TS / 2 || this.isBlocked(tX.x, tX.y)) nx = this.player.x;
    const tY = foot(nx, ny);
    if (ny < TS || this.isBlocked(tY.x, tY.y)) ny = this.player.y;
    this.player.setPosition(nx, ny);
    this.playerTile = foot(nx, ny);
    store.update((s) => (s.player = { ...this.playerTile }));
  }

  /** End a pinch when a finger lifts; snap to an integer zoom for crisp pixels. */
  private onPointerUp(): void {
    if (this.dragArmed && this.dragMoved) {
      this.faceIdle();
      store.update((s) => (s.player = { ...this.playerTile }));
    }
    this.dragArmed = false;
    if (this.pinching && !(this.input.pointer1.isDown && this.input.pointer2.isDown)) {
      this.pinching = false;
      this.pinchStartDist = null;
      this.cameras.main.setZoom(Phaser.Math.Clamp(Math.round(this.cameras.main.zoom), CONFIG.minZoom, CONFIG.maxZoom));
    }
  }

  /** A small expanding ring at the touch point — instant tap feedback (no iOS haptics on web). */
  private tapRipple(wx: number, wy: number): void {
    const ring = this.add.image(wx, wy, TX.moveMarker).setDepth(8500).setScale(0.3).setAlpha(0.7);
    this.tweens.add({ targets: ring, scale: 0.9, alpha: 0, duration: 300, ease: 'Quad.easeOut', onComplete: () => ring.destroy() });
  }

  private installDevKeys(): void {
    const moves: Record<string, [number, number]> = {
      ArrowUp: [0, -1], KeyW: [0, -1],
      ArrowDown: [0, 1], KeyS: [0, 1],
      ArrowLeft: [-1, 0], KeyA: [-1, 0],
      ArrowRight: [1, 0], KeyD: [1, 0],
    };
    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => {
      const d = moves[e.code];
      if (!d) return;
      const nx = this.playerTile.x + d[0];
      const ny = this.playerTile.y + d[1];
      if (!this.isBlocked(nx, ny)) {
        this.path = [{ x: nx, y: ny }];
        this.pendingArrival = null;
      }
    });
  }

  private registerCornerTap(): void {
    const now = this.time.now;
    if (now - this.cornerTapAt > 1500) this.cornerTaps = 0;
    this.cornerTapAt = now;
    this.cornerTaps += 1;
    if (this.cornerTaps >= CONFIG.debugCornerTaps) {
      this.cornerTaps = 0;
      if (!this.scene.isActive('Debug')) this.scene.launch('Debug');
    }
  }

  // ---- tap routing ------------------------------------------------------------

  private handleTap(tile: TileXY): void {
    // structures (player routes to an adjacent tile, then acts)
    if (this.isHouseTile(tile)) return this.routeToInteractable(this.houseDoor(), () => this.enterInterior());
    if (this.isShopTile(tile)) return this.routeToInteractable(this.shopFront(), () => this.ui.openStall());
    const bn = this.bPos('barn');
    if (tile.x >= bn.x && tile.x < bn.x + 2 && tile.y >= bn.y && tile.y < bn.y + 2) {
      return this.routeToInteractable(bn, () => this.ui.openBarnUpgrade());
    }
    const mb = this.bPos('mailbox');
    if (tile.x === mb.x && tile.y === mb.y) return this.routeToInteractable(mb, () => this.collectMail());
    const tr = this.bPos('trail');
    if (tile.x === tr.x && tile.y === tr.y) return this.routeToInteractable(tr, () => this.enterForest());
    const sg = this.bPos('sign');
    if (tile.x === sg.x && tile.y === sg.y) return this.routeToInteractable(sg, () => this.tapSign());

    const clT = this.tileOf(this.clara);
    if (tile.x === clT.x && tile.y === clT.y) {
      const giftId = this.ui.selectedGiftId;
      return this.routeToTile(tile, () => (giftId ? this.giveClara(giftId) : this.claraSpeak()));
    }

    const animal = this.animalAt(tile);
    if (animal && (animal.type === 'cow' || animal.type === 'sheep')) {
      return this.routeToTile(tile, () => this.interactAnimal(animal));
    }

    if (this.waterAt(tile)) return this.routeToInteractable(tile, () => this.cast(tile));

    // tend the land (chopping trees needs the axe tool — avoids accidents)
    if (this.weedAt(tile)) return this.routeToTile(tile, () => this.pullWeed(tile));
    if (this.eggAt(tile)) return this.routeToTile(tile, () => this.collectEgg(tile));
    if (this.cropAt(tile.x, tile.y)) return this.routeToTile(tile, () => this.actOnCrop(tile));
    if (this.tilledAt(tile.x, tile.y)) return this.routeToTile(tile, () => this.actOnSoil(tile));
    // open grass: till + plant in one tap ONLY when a seed is selected; else just walk there
    if (this.canTill(tile) && this.ui.selectedSeedId) {
      return this.routeToTile(tile, () => this.tillAndPlant(tile));
    }

    // pet the pet only when nothing else is on that tile (so it never blocks an action)
    const petT = this.tileOf(this.pet);
    if (tile.x === petT.x && tile.y === petT.y) return this.routeToTile(tile, () => this.petThePet());

    // plain move — never alters a tile
    if (!this.isBlocked(tile.x, tile.y)) this.routeToTile(tile, null);
  }

  private isHouseTile(t: TileXY): boolean {
    const p = this.bPos('house');
    const b = this.bDef('house');
    return t.x >= p.x && t.x < p.x + b.w && t.y >= p.y && t.y < p.y + b.h;
  }
  private isShopTile(t: TileXY): boolean {
    const p = this.bPos('shop');
    const b = this.bDef('shop');
    return t.x >= p.x && t.x < p.x + b.w && t.y >= p.y && t.y < p.y + b.h;
  }

  /** Explicit-tool tap: force one action on the tapped tile. */
  private useTool(tool: ToolId, tile: TileXY): void {
    if (tool !== 'move') this.movingBuilding = null; // forget a half-started building move
    switch (tool) {
      case 'hoe':
        if (this.cropAt(tile.x, tile.y)) this.ui.showToast('A crop is growing there', 'info');
        else if (this.tilledAt(tile.x, tile.y)) this.routeToTile(tile, () => this.untill(tile));
        else if (this.flowerAt(tile.x, tile.y)) this.routeToTile(tile, () => this.clearFlower(tile));
        else if (this.canTill(tile)) this.routeToTile(tile, () => this.tillTile(tile));
        else this.ui.showToast('Hoe: tap grass, soil, or a flower', 'info');
        break;
      case 'water':
        if (this.cropAt(tile.x, tile.y)) this.routeToTile(tile, () => this.actOnCrop(tile));
        else this.ui.showToast('Watering can: tap a crop', 'info');
        break;
      case 'axe':
        if (this.treeAt(tile.x, tile.y)) this.routeToInteractable(tile, () => this.chopTree(tile));
        else this.ui.showToast('Axe: tap a tree', 'info');
        break;
      case 'scythe':
        if (this.weedAt(tile)) this.routeToTile(tile, () => this.pullWeed(tile));
        else if (this.flowerAt(tile.x, tile.y)) this.routeToTile(tile, () => this.clearFlower(tile));
        else this.ui.showToast('Scythe: tap a weed or flower', 'info');
        break;
      case 'rod':
        if (this.waterAt(tile)) this.routeToInteractable(tile, () => this.cast(tile));
        else this.ui.showToast('Rod: tap the pond', 'info');
        break;
      case 'move': {
        if (this.movingBuilding) {
          this.tryMoveBuilding(this.movingBuilding, tile);
          break;
        }
        const bid = this.buildingAt(tile);
        if (bid) {
          this.movingBuilding = bid;
          this.ui.showToast(`Moving the ${bid} — tap a new spot`, 'info');
          break;
        }
        this.moveDeco(tile);
        break;
      }
      default:
        this.handleTap(tile);
    }
  }

  /** Pick up a placed decoration/path/fence back into the bag, to re-place it. */
  private moveDeco(tile: TileXY): void {
    const decos = store.get().decorations;
    const idx = decos.findIndex((p) => {
      const def = getDecoration(p.decoId);
      return def != null && tile.x >= p.x && tile.x < p.x + def.w && tile.y >= p.y && tile.y < p.y + def.h;
    });
    if (idx < 0) {
      this.ui.showToast('Nothing to move here', 'info');
      return;
    }
    const decoId = decos[idx]!.decoId;
    store.update((s) => s.decorations.splice(idx, 1));
    store.addItem(decoId, 1);
    playSfx(this, AUDIO.pop);
    this.renderWorldFromState();
    this.ui.showToast('Picked up — place it again', 'good');
  }

  private tileOf(img: Phaser.GameObjects.Image): TileXY {
    return { x: Math.floor(img.x / TS), y: Math.floor((img.y - 1) / TS) };
  }

  private routeToTile(tile: TileXY, action: (() => void) | null): void {
    if (this.playerTile.x === tile.x && this.playerTile.y === tile.y) {
      action?.();
      return;
    }
    const path = findPath(this.playerTile, tile, MAP_WIDTH, MAP_HEIGHT, this.isBlocked);
    if (!path) {
      this.ui.showToast("Can't reach there", 'warn');
      return;
    }
    this.path = path;
    this.pendingArrival = action;
    this.showMarker(tile.x, tile.y);
  }

  private routeToInteractable(target: TileXY, action: () => void): void {
    const md = Math.abs(this.playerTile.x - target.x) + Math.abs(this.playerTile.y - target.y);
    if (md <= 1) {
      action();
      return;
    }
    const adj = adjacentWalkable(target, this.playerTile, MAP_WIDTH, MAP_HEIGHT, this.isBlocked);
    if (!adj) {
      this.ui.showToast("Can't reach there", 'warn');
      return;
    }
    const path = findPath(this.playerTile, adj, MAP_WIDTH, MAP_HEIGHT, this.isBlocked);
    if (!path) {
      this.ui.showToast("Can't reach there", 'warn');
      return;
    }
    this.path = path;
    this.pendingArrival = action;
    this.showMarker(target.x, target.y);
  }

  private showMarker(tx: number, ty: number): void {
    this.moveMarker.setPosition(tx * TS + TS / 2, ty * TS + TS / 2).setAlpha(0.9).setScale(1);
    this.tweens.killTweensOf(this.moveMarker);
    this.tweens.add({ targets: this.moveMarker, alpha: 0, scale: 0.6, duration: 450 });
  }

  // ---- actions ----------------------------------------------------------------

  private trySpend(action: EnergyAction): boolean {
    // route through the store so farm actions also advance the clock (time-blend) + respect the winter cost
    if (!store.trySpendEnergy(action)) {
      this.ui.showToast('Too tired — time to sleep', 'warn');
      return false;
    }
    this.actionBob();
    return true;
  }

  /** A quick squash-and-stretch on the player so actions (pull/till/chop/water/fish) have some weight. */
  private actionBob(): void {
    this.player.setScale(1, 1);
    this.tweens.add({
      targets: this.player,
      scaleX: 1.12,
      scaleY: 0.85,
      duration: 95,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => this.player.setScale(1, 1),
    });
  }

  /** Play a one-shot real tool animation on the player, then restore the walk sheet. */
  private playerAction(kind: 'swing' | 'water'): void {
    const key = kind === 'water' ? 'act-water' : 'act-swing';
    if (!this.anims.exists(key)) return; // no action sheet loaded — the squash-bob already gave feedback
    this.player.setTexture(TX.playerAction);
    this.player.play(key);
    this.player.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => this.faceIdle());
  }

  /** Play the walk animation matching the movement direction (down/up/side), restoring the walk sheet. */
  private faceMove(dx: number, dy: number): void {
    if (this.player.texture.key === TX.playerAction) this.player.setTexture(TX.player);
    let key: string;
    if (Math.abs(dx) > Math.abs(dy)) {
      key = this.anims.exists('walk-side') ? 'walk-side' : 'player-walk';
      this.player.setFlipX(dx < 0);
      this.lastFacing = 'side';
    } else if (dy < 0) {
      key = this.anims.exists('walk-up') ? 'walk-up' : 'player-walk';
      this.lastFacing = 'up';
    } else {
      key = this.anims.exists('walk-down') ? 'walk-down' : 'player-walk';
      this.lastFacing = 'down';
    }
    if (this.anims.exists(key) && (this.player.anims.currentAnim?.key !== key || !this.player.anims.isPlaying)) {
      this.player.play(key);
    }
  }

  /** Stop on the idle frame for the last-faced direction. */
  private faceIdle(): void {
    if (this.player.texture.key === TX.playerAction) this.player.setTexture(TX.player);
    this.player.stop();
    this.player.setFrame(!this.anims.exists('walk-up') ? 0 : this.lastFacing === 'up' ? 4 : this.lastFacing === 'side' ? 8 : 0);
  }

  private tillTile(tile: TileXY): boolean {
    if (this.tilledAt(tile.x, tile.y) || this.cropAt(tile.x, tile.y)) return false;
    if (!this.trySpend('till')) return false;
    store.update((s) => s.tilled.push({ x: tile.x, y: tile.y }));
    this.soilLayer.putTileAt(SOIL_DRY, tile.x, tile.y);
    playSfx(this, AUDIO.till);
    this.playerAction('swing');
    return true;
  }

  /** Hoe a tilled (un-planted) tile back to grass. */
  private untill(tile: TileXY): void {
    store.update((s) => (s.tilled = s.tilled.filter((t) => !(t.x === tile.x && t.y === tile.y))));
    this.soilLayer.putTileAt(-1, tile.x, tile.y);
    playSfx(this, AUDIO.till);
    this.ui.showToast('Cleared the soil', 'info');
  }

  /** Clear a decorative flower tile back to grass (persists via the chopped list). */
  private clearFlower(tile: TileXY): void {
    this.objectsLayer.removeTileAt(tile.x, tile.y);
    store.update((s) => s.chopped.push({ x: tile.x, y: tile.y }));
    playSfx(this, AUDIO.weed);
    this.ui.showToast('Cleared the flowers', 'info');
  }

  /** Convenience: tap garden grass with a seed selected → till then plant. */
  private tillAndPlant(tile: TileXY): void {
    if (this.tilledAt(tile.x, tile.y)) {
      this.actOnSoil(tile);
      return;
    }
    if (this.tillTile(tile)) this.actOnSoil(tile);
  }

  private actOnSoil(tile: TileXY): void {
    const seedId = this.ui.selectedSeedId;
    if (!seedId) {
      this.ui.showToast('Pick a seed from your bag', 'info');
      return;
    }
    const item = getItem(seedId);
    if (!item || !item.plantsCrop) return;
    if (store.countItem(seedId) <= 0) return;
    if (!this.trySpend('plant')) return;

    store.removeItem(seedId, 1);
    const crop = newPlantedCrop(item.plantsCrop, tile.x, tile.y, store.get().clock.day);
    store.update((s) => s.crops.push(crop));
    this.addOrUpdateCropSprite(crop);
    playSfx(this, AUDIO.plant);
    if (!cropInSeason(item.plantsCrop, seasonOf(store.get().clock.day).id)) {
      this.ui.showToast(`${getCrop(item.plantsCrop)?.name ?? 'This'} grows slowly out of season 🍂`, 'info');
    }
    if (store.countItem(seedId) <= 0) this.ui.clearSelection();
  }

  private actOnCrop(tile: TileXY): void {
    const crop = this.cropAt(tile.x, tile.y);
    if (!crop) return;
    const def = getCrop(crop.cropId);
    if (!def) return;

    if (isHarvestable(crop, def)) {
      if (!this.trySpend('harvest')) return;
      const result = harvestCrop(crop, def);
      if (!result) return;
      const great = Math.random() < 0.2; // a lucky "great harvest"
      const offSeason = !cropInSeason(crop.cropId, seasonOf(store.get().clock.day).id);
      const got = Math.max(1, result.qty + (great ? 1 : 0) - (offSeason ? 1 : 0)); // smaller pick out of season
      store.addItem(result.produceItemId, got);
      if (great) {
        this.spawnHearts(tile.x * TS + TS / 2, tile.y * TS);
        this.burst(tile.x * TS + TS / 2, tile.y * TS + TS / 2, TX.sparkle, 7);
        this.cameras.main.shake(90, 0.0025); // gentler — was a bit too punchy for pulling crops
      }
      store.update((s) => {
        s.stats.cropsHarvested += got;
        if (result.crop) {
          const i = s.crops.findIndex((c) => c.x === tile.x && c.y === tile.y);
          if (i >= 0) s.crops[i] = result.crop;
        } else {
          s.crops = s.crops.filter((c) => !(c.x === tile.x && c.y === tile.y));
        }
      });
      if (result.crop) {
        this.addOrUpdateCropSprite(result.crop);
      } else {
        this.cropSprites.get(tileKey(tile.x, tile.y))?.destroy();
        this.cropSprites.delete(tileKey(tile.x, tile.y));
      }
      this.soilLayer.putTileAt(SOIL_DRY, tile.x, tile.y);
      playSfx(this, AUDIO.harvest);
      store.logJournal('Picked my very first crop 🌱');
      this.ui.showToast(
        offSeason ? `+${got} ${def.name} — out of season 🍂` : great ? `⭐ Great harvest! +${got} ${def.name}` : `+${got} ${def.name}`,
        offSeason ? 'info' : 'good',
      );
      return;
    }

    // not ready → water it
    if (crop.watered) {
      this.ui.showToast('Already watered', 'info');
      return;
    }
    if (!this.trySpend('water')) return;
    this.playerAction('water');
    store.update((s) => {
      const i = s.crops.findIndex((c) => c.x === tile.x && c.y === tile.y);
      if (i >= 0) s.crops[i] = waterCrop(s.crops[i]!);
    });
    this.soilLayer.putTileAt(SOIL_WET, tile.x, tile.y);
    playSfx(this, AUDIO.water);
  }

  // ---- weeds & trees ----------------------------------------------------------

  private weedAt(t: TileXY): boolean {
    return store.get().weeds.some((w) => w.x === t.x && w.y === t.y);
  }
  private treeAt(x: number, y: number): boolean {
    const t = this.objectsLayer.getTileAt(x, y);
    return !!t && t.index === TREE_GID;
  }

  private flowerAt(x: number, y: number): boolean {
    const t = this.objectsLayer.getTileAt(x, y);
    return !!t && t.index === FLOWER_GID;
  }

  private pullWeed(tile: TileXY): void {
    if (!this.weedAt(tile) || !this.trySpend('pullWeed')) return;
    store.update((s) => {
      s.weeds = s.weeds.filter((w) => !(w.x === tile.x && w.y === tile.y));
    });
    this.weedSprites.get(tileKey(tile.x, tile.y))?.destroy();
    this.weedSprites.delete(tileKey(tile.x, tile.y));
    playSfx(this, AUDIO.weed);
  }

  /** A one-shot particle burst (dust, sparkles…). */
  private burst(x: number, y: number, tex: string, n: number): void {
    const p = this.add
      .particles(x, y, tex, {
        speed: { min: 20, max: 70 },
        angle: { min: 200, max: 340 },
        scale: { start: 1.3, end: 0 },
        lifespan: 420,
        quantity: n,
        emitting: false,
      })
      .setDepth(9000);
    p.explode(n);
    this.time.delayedCall(550, () => p.destroy());
  }

  /** Fertilizer: bump every still-growing crop one day; returns how many grew. */
  fertilizeAll(): number {
    let n = 0;
    store.update((s) => {
      for (const c of s.crops) {
        const def = getCrop(c.cropId);
        if (def && c.growth < def.growthDays) {
          c.growth = Math.min(def.growthDays, c.growth + 1);
          n++;
        }
      }
    });
    for (const c of store.get().crops) this.addOrUpdateCropSprite(c);
    return n;
  }

  private chopTree(tile: TileXY): void {
    if (!this.treeAt(tile.x, tile.y) || !this.trySpend('chop')) return;
    this.objectsLayer.removeTileAt(tile.x, tile.y);
    this.mapBlocked.delete(tileKey(tile.x, tile.y));
    store.update((s) => s.chopped.push({ x: tile.x, y: tile.y }));
    store.addItem('wood', 1);
    playSfx(this, AUDIO.chop);
    this.playerAction('swing');
    this.burst(tile.x * TS + TS / 2, tile.y * TS + TS / 2, TX.dust, 8);
    this.ui.showToast('+1 Wood', 'good');
  }

  private addWeedSprite(w: TileXY): void {
    const img = this.add.image(w.x * TS + TS / 2, w.y * TS + TS, TX.weed).setOrigin(0.5, 1);
    img.setDepth(w.y * TS + TS - 1);
    this.weedSprites.set(tileKey(w.x, w.y), img);
  }

  private spawnWeeds(): void {
    const scarecrows = store.get().decorations.filter((d) => d.decoId === 'scarecrow');
    let added = 0;
    let tries = 0;
    while (added < CONFIG.weedsPerDay && store.get().weeds.length < CONFIG.weedCap && tries < 80) {
      tries += 1;
      const x = Phaser.Math.Between(1, MAP_WIDTH - 2);
      const y = Phaser.Math.Between(1, MAP_HEIGHT - 2);
      if (this.isBlocked(x, y) || this.tilledAt(x, y) || this.cropAt(x, y) || this.weedAt({ x, y })) {
        continue;
      }
      if (scarecrows.some((sc) => Math.abs(sc.x - x) <= 3 && Math.abs(sc.y - y) <= 3)) continue; // scarecrows keep weeds away
      store.update((s) => s.weeds.push({ x, y }));
      added += 1;
    }
  }

  /** Sprinklers water the crops around them (3×3) each morning. */
  private applySprinklers(): void {
    const radii: Record<string, number> = { sprinkler: 1, copper_sprinkler: 2, iron_sprinkler: 3 };
    const sprinklers = store.get().decorations.filter((d) => radii[d.decoId] !== undefined);
    if (sprinklers.length === 0) return;
    store.update((s) => {
      s.crops = s.crops.map((c) => {
        const wet = sprinklers.some((sp) => {
          const r = radii[sp.decoId] ?? 1;
          return Math.abs(sp.x - c.x) <= r && Math.abs(sp.y - c.y) <= r;
        });
        return wet ? { ...c, watered: true } : c;
      });
    });
  }

  // ---- animals (chickens & eggs) ----------------------------------------------

  buyAnimal(type: string): void {
    const a: Animal = {
      id: `a${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`,
      type,
      x: COOP.x,
      y: COOP.y,
      lastEggDay: 0,
      happy: store.get().barnLevel * 2, // a nicer barn → happier animals → more produce
    };
    store.update((s) => s.animals.push(a));
    this.addAnimalSprite(a);
    store.logJournal(`Welcomed a ${type} to the farm 🐮`);
    this.ui.showToast(`A new ${type} joins the farm!`, 'good');
  }

  /** Idle animations for the real cow/chicken sheets (skipped if only the placeholder texture exists). */
  private ensureAnimalAnims(): void {
    if (this.textures.get(TX.cow).frameTotal >= 3 && !this.anims.exists('cow-idle')) {
      this.anims.create({ key: 'cow-idle', frames: this.anims.generateFrameNumbers(TX.cow, { frames: [0, 1, 2] }), frameRate: 3, repeat: -1 });
    }
    if (this.textures.get(TX.chicken).frameTotal >= 2 && !this.anims.exists('chicken-idle')) {
      this.anims.create({ key: 'chicken-idle', frames: this.anims.generateFrameNumbers(TX.chicken, { frames: [0, 1] }), frameRate: 3, repeat: -1 });
    }
  }

  private addAnimalSprite(a: Animal): void {
    this.ensureAnimalAnims();
    const key =
      a.type === 'cow' ? TX.cow : a.type === 'sheep' ? TX.sheep : a.type === 'pig' ? TX.pig : a.type === 'duck' ? TX.duck : TX.chicken;
    const sp = this.add.sprite(a.x * TS + TS / 2, a.y * TS + TS, key, 0).setOrigin(0.5, 1);
    if (a.type === 'cow') {
      sp.setScale(0.8); // the cow sheet is 32×32 frames
      if (this.anims.exists('cow-idle')) sp.play('cow-idle');
    } else if (a.type === 'chicken') {
      if (this.anims.exists('chicken-idle')) sp.play('chicken-idle');
    } else if (a.type === 'sheep') {
      sp.setScale(1.25);
    } else if (a.type === 'pig') {
      sp.setScale(1.3);
    } else if (a.type === 'duck') {
      sp.setScale(1.05);
    }
    sp.setDepth(a.y * TS + TS);
    this.animalSprites.set(a.id, sp);
    this.animalWander.set(a.id, { target: null, wait: Phaser.Math.Between(400, 1800) });
  }

  private animalAt(t: TileXY): Animal | undefined {
    return store.get().animals.find((a) => a.x === t.x && a.y === t.y);
  }

  private animalProduce(a: Animal): { item: string; sfx: AudioKey; label: string } | null {
    if (a.type === 'cow') return { item: 'milk', sfx: AUDIO.moo, label: '+1 Milk' };
    if (a.type === 'sheep') return { item: 'wool', sfx: AUDIO.baa, label: '+1 Wool' };
    if (a.type === 'pig') return { item: 'truffle', sfx: AUDIO.oink, label: '+1 Truffle' };
    if (a.type === 'duck') return { item: 'duck_egg', sfx: AUDIO.quack, label: '+1 Duck Egg' };
    return null;
  }

  /** Milk a cow / shear a sheep — once per morning. */
  private interactAnimal(a: Animal): void {
    const p = this.animalProduce(a);
    if (!p) return;
    const day = store.get().clock.day;
    if (a.lastEggDay >= day) {
      this.ui.showToast('Already collected today', 'info');
      return;
    }
    if (!this.trySpend('milk')) return;
    a.lastEggDay = day;
    a.happy = Math.min(10, (a.happy ?? 0) + 1); // daily care → happier
    const qty = (a.happy ?? 0) >= 5 && Math.random() < 0.4 ? 2 : 1; // a happy animal gives extra
    store.addItem(p.item, qty);
    playSfx(this, p.sfx);
    this.spawnHearts(a.x * TS + TS / 2, a.y * TS);
    this.ui.showToast(qty > 1 ? `${p.label} ×2 — so happy! 💛` : p.label, 'good');
  }

  // ---- mailbox ----------------------------------------------------------------

  private collectMail(): void {
    const mail = store.get().mail;
    if (!mail) {
      this.ui.showToast('The mailbox is empty', 'info');
      return;
    }
    if (mail.gift === 'letter') {
      store.update((s) => (s.mail = null));
      this.updateMailFlag();
      playSfx(this, AUDIO.pop);
      this.ui.showLetter(personalization.letters[mail.qty] ?? '💛');
      return;
    }
    if (mail.gift === 'gold') {
      store.update((s) => {
        s.money += mail.qty;
        s.mail = null;
      });
      this.ui.showToast(`${personalization.clara.name} left you ${mail.qty}g 💛`, 'good');
    } else {
      store.addItem(mail.gift, mail.qty);
      store.update((s) => (s.mail = null));
      const name = getItem(mail.gift)?.name ?? mail.gift;
      this.ui.showToast(`${personalization.clara.name} left you ${mail.qty}× ${name} 💛`, 'good');
    }
    playSfx(this, AUDIO.coin);
    this.updateMailFlag();
  }

  /** Each morning, a chance Clara leaves a little gift in the mailbox. */
  private rollMail(): void {
    if (store.get().mail) return; // keep uncollected mail
    if (Math.random() > 0.5) return;
    let gift: { gift: string; qty: number };
    const r = Math.random();
    if (r < 0.25 && personalization.letters.length > 0) {
      gift = { gift: 'letter', qty: Math.floor(Math.random() * personalization.letters.length) };
    } else if (r < 0.6) {
      gift = { gift: 'gold', qty: Phaser.Math.Between(4, 10) * 10 };
    } else {
      const seeds = Object.values(CROPS).map((c) => c.seedItemId);
      gift = { gift: seeds[Math.floor(Math.random() * seeds.length)]!, qty: Phaser.Math.Between(1, 3) };
    }
    store.update((s) => (s.mail = gift));
    this.updateMailFlag();
  }

  private updateMailFlag(): void {
    this.mailFlag?.setVisible(store.get().mail !== null);
  }

  private addEggSprite(e: TileXY): void {
    const img = this.add.image(e.x * TS + TS / 2, e.y * TS + TS - 1, TX.egg).setOrigin(0.5, 1);
    img.setDepth(e.y * TS + TS - 1);
    this.eggSprites.set(tileKey(e.x, e.y), img);
  }

  private updateAnimals(delta: number): void {
    for (const a of store.get().animals) {
      const sp = this.animalSprites.get(a.id);
      const w = this.animalWander.get(a.id);
      if (!sp || !w) continue;
      if (w.target) {
        const fx = w.target.x * TS + TS / 2;
        const fy = w.target.y * TS + TS;
        const dx = fx - sp.x;
        const dy = fy - sp.y;
        const d = Math.hypot(dx, dy);
        const step = (1.3 * TS * delta) / 1000;
        if (Math.abs(dx) > 0.4) sp.setFlipX(dx < 0);
        if (d <= step) {
          sp.setPosition(fx, fy);
          a.x = w.target.x; // mutate directly — no UI churn; persists on save
          a.y = w.target.y;
          w.target = null;
          w.wait = Phaser.Math.Between(600, 2600);
        } else {
          sp.x += (dx / d) * step;
          sp.y += (dy / d) * step;
        }
        sp.setDepth(sp.y);
        continue;
      }
      w.wait -= delta;
      if (w.wait > 0) continue;
      const dirs = [[0, -1], [0, 1], [-1, 0], [1, 0]].sort(() => Math.random() - 0.5);
      for (const [ox, oy] of dirs) {
        const nx = a.x + ox!;
        const ny = a.y + oy!;
        if (
          Math.abs(nx - COOP.x) <= COOP_RADIUS &&
          Math.abs(ny - COOP.y) <= COOP_RADIUS &&
          !this.isBlocked(nx, ny)
        ) {
          w.target = { x: nx, y: ny };
          break;
        }
      }
      if (!w.target) w.wait = 800;
    }
  }

  /** Each animal lays one egg on the morning of a new day. */
  private layEggs(): void {
    const day = store.get().clock.day;
    store.update((s) => {
      for (const a of s.animals) {
        if (a.type !== 'chicken' || a.lastEggDay >= day) continue;
        a.lastEggDay = day;
        if (!s.eggs.some((e) => e.x === a.x && e.y === a.y)) s.eggs.push({ x: a.x, y: a.y });
      }
    });
  }

  private eggAt(t: TileXY): boolean {
    return store.get().eggs.some((e) => e.x === t.x && e.y === t.y);
  }

  private collectEgg(tile: TileXY): void {
    if (!this.eggAt(tile)) return;
    store.update((s) => {
      s.eggs = s.eggs.filter((e) => !(e.x === tile.x && e.y === tile.y));
    });
    store.addItem('egg', 1);
    this.eggSprites.get(tileKey(tile.x, tile.y))?.destroy();
    this.eggSprites.delete(tileKey(tile.x, tile.y));
    playSfx(this, AUDIO.pop);
    this.ui.showToast('+1 Egg', 'good');
  }

  /** Advance growable plants each morning; mature ones bear fruit. */
  private growGroves(): void {
    store.update((s) => {
      for (const p of s.decorations) {
        const def = getDecoration(p.decoId);
        if (!def || !def.grows) continue;
        p.growth = Math.min(def.grows.days, (p.growth ?? 0) + 1);
      }
    });
    for (const p of store.get().decorations) {
      const def = getDecoration(p.decoId);
      if (def && def.grows && (p.growth ?? 0) >= def.grows.days) store.addItem(def.grows.fruit, 1);
    }
  }

  // ---- weather ----------------------------------------------------------------

  private rollWeather(): void {
    const season = seasonOf(store.get().clock.day).id;
    const odds: Record<string, number> = { spring: 0.35, summer: 0.15, fall: 0.3, winter: 0.25 };
    let chance = odds[season] ?? 0.2;
    if (store.get().weather === 'rain') chance *= 0.35; // far less likely to rain two days running — no more weeks of rain
    const rain = Math.random() < chance;
    store.update((s) => (s.weather = rain ? 'rain' : 'clear'));
    if (rain) {
      store.update((s) => {
        s.crops = s.crops.map((c) => ({ ...c, watered: true }));
      });
      this.ui.showToast('Rainy day — your crops watered themselves 🌧️', 'good');
    }
    this.applyWeather();
  }

  private applyWeather(): void {
    const raining = store.get().weather === 'rain';
    const winter = seasonOf(store.get().clock.day).id === 'winter';
    this.ui.showRain(raining && !winter); // winter shows snow instead
    setRain(raining && !winter);
  }

  // ---- decoration placement ---------------------------------------------------

  enterPlacement(decoId: string): void {
    const def = getDecoration(decoId);
    if (!def) return;
    this.placementDeco = decoId;
    this.ghost?.destroy();
    this.ghost = this.add
      .image(this.player.x, this.player.y, def.texture)
      .setOrigin(0, 0)
      .setAlpha(0.6)
      .setDepth(9500);
    this.ui.showToast('Tap a spot to place', 'info');
  }

  exitPlacement(): void {
    this.placementDeco = null;
    this.ghost?.destroy();
    this.ghost = null;
  }

  private tryPlace(tile: TileXY): void {
    const decoId = this.placementDeco;
    if (!decoId) return;
    const def = getDecoration(decoId);
    if (!def) return;

    for (let dx = 0; dx < def.w; dx++) {
      for (let dy = 0; dy < def.h; dy++) {
        const tx = tile.x + dx;
        const ty = tile.y + dy;
        const okBridge = decoId === 'bridge' && this.hasWaterDeco(tx, ty) && !this.mapBlocked.has(tileKey(tx, ty));
        if (!okBridge && (tx >= MAP_WIDTH || ty >= MAP_HEIGHT || this.isBlocked(tx, ty) || this.cropAt(tx, ty) || this.tilledAt(tx, ty))) {
          this.ui.showToast("Can't place there", 'warn');
          return;
        }
      }
    }
    if (store.countItem(decoId) <= 0) return;

    store.removeItem(decoId, 1);
    const placed: PlacedDecoration = { decoId, x: tile.x, y: tile.y, growth: def.grows ? 0 : undefined };
    store.update((s) => s.decorations.push(placed));
    const img = this.add.image(tile.x * TS, tile.y * TS, this.decoTexture(def, placed)).setOrigin(0, 0);
    img.setDepth(def.ground ? 1 : (tile.y + def.h) * TS);
    this.decoSprites.push(img);
    if (def.solid) this.rebuildBlocked();
    playSfx(this, AUDIO.pop);

    if (store.countItem(decoId) <= 0) {
      this.exitPlacement();
      this.ui.clearSelection();
    }
  }

  // ---- inhabitants ------------------------------------------------------------

  private petTexture(): string {
    return store.get().petType === 'dog' ? TX.petDog : TX.petCat;
  }

  /** Live-swap the pet sprite (Settings pet toggle). */
  applyPetTexture(): void {
    this.pet.setTexture(this.petTexture());
  }

  private petThePet(): void {
    this.spawnHearts(this.pet.x, this.pet.y - 12);
    playSfx(this, store.get().petType === 'dog' ? AUDIO.bark : AUDIO.meow);
    this.tweens.add({ targets: this.pet, scaleX: 1.15, scaleY: 0.9, duration: 120, yoyo: true });
    store.addEnergy(1); // a little pick-me-up
    store.update((s) => (s.petHearts += 1));
    const name = store.get().petName;
    if (name) this.showBubble(this.pet.x, this.pet.y - 14, `${name} 💛`, 1200);
  }

  private claraSpeak(): void {
    if (store.get().claraHearts >= 10 && Math.random() < 0.25) {
      this.spawnHearts(this.clara.x, this.clara.y - 12);
      store.addEnergy(6);
      this.showBubble(this.clara.x, this.clara.y - 14, 'Hug! 🤗', 1800);
      playSfx(this, AUDIO.baby);
      return;
    }
    const mommy = ['Mommy!', 'Mommy 💛', 'Hi Mommy!', 'Mama!'];
    const phrases = personalization.clara.phrases;
    const say =
      Math.random() < 0.4
        ? mommy[Math.floor(Math.random() * mommy.length)]!
        : phrases[Math.floor(Math.random() * phrases.length)] ?? '…';
    this.showBubble(this.clara.x, this.clara.y - 14, say, 1900);
    playSfx(this, AUDIO.baby);
    store.addEnergy(1);
  }

  /** Give Clara a produce/dish (selected in the bag) → friendship hearts. Granting her wish gives extra. */
  private giveClara(itemId: string): void {
    if (store.countItem(itemId) <= 0) return;
    store.removeItem(itemId, 1);
    const cn = personalization.clara.name;
    const wished = itemId === this.claraCraving;
    const favorite = wished || ['cake', 'fruit_bowl', 'strawberry', 'berry', 'milk', 'apple'].includes(itemId);
    store.update((s) => (s.claraHearts += wished ? 3 : favorite ? 2 : 1));
    this.spawnHearts(this.clara.x, this.clara.y - 12);
    if (wished) this.spawnHearts(this.clara.x - 8, this.clara.y - 16);
    playSfx(this, AUDIO.baby);
    const hearts = store.get().claraHearts;
    const name = getItem(itemId)?.name ?? 'gift';
    this.showBubble(this.clara.x, this.clara.y - 14, wished ? '🥰💛💛' : favorite ? '💛💛' : '💛', 1500);
    this.ui.showToast(
      wished ? `${cn}'s wish came true — ${name}! 💛 ×${hearts}` : `${cn} ${favorite ? 'LOVES' : 'loves'} the ${name}! 💛 ×${hearts}`,
      'good',
    );
    this.ui.clearSelection();
    if (wished) {
      this.claraCraving = null;
      this.updateClaraThought();
      this.time.delayedCall(9000, () => this.rollClaraCraving());
    }
    this.claraMilestone(hearts);
  }

  /** Clara starts wanting a random treat — shown as a thought above her head + a little ask. */
  private rollClaraCraving(): void {
    const foods = ['pumpkin_pie', 'omelette', 'fruit_bowl', 'veggie_stew', 'garden_salad', 'strawberry', 'apple', 'milk', 'cake', 'honey', 'berry', 'melon', 'butterfly'];
    this.claraCraving = foods[Math.floor(Math.random() * foods.length)]!;
    this.updateClaraThought();
    this.showBubble(this.clara.x, this.clara.y - 14, `I want ${getItem(this.claraCraving)?.name ?? 'a treat'}! 🥺`, 2600);
  }

  private updateClaraThought(): void {
    this.claraThought?.destroy();
    this.claraThought = undefined;
    if (!this.claraCraving) return;
    const def = getItem(this.claraCraving);
    if (!def) return;
    this.claraThought = this.add.image(this.clara.x, this.clara.y - 28, def.icon).setScale(0.85).setDepth(9000);
  }

  private claraMilestone(hearts: number): void {
    const rewards: Record<number, number> = { 5: 100, 10: 200, 20: 350, 30: 500 };
    const gold = rewards[hearts];
    if (!gold) return;
    store.update((s) => (s.money += gold));
    playSfx(this, AUDIO.coin);
    this.ui.showToast(`Clara made you a thank-you gift! +${gold}g 💛`, 'good');
  }

  /** The welcome sign — a fair-prize claim on festival days, else the sign text. */
  private tapSign(): void {
    const day = store.get().clock.day;
    if (isFestivalDay(day)) {
      const flag = `fair_y${yearOf(day)}_${seasonOf(day).id}`;
      if (!store.get().flags[flag]) {
        const seeds = Object.values(CROPS).map((c) => c.seedItemId);
        const seed = seeds[Math.floor(Math.random() * seeds.length)]!;
        store.update((s) => {
          s.money += 150;
          s.flags[flag] = true;
        });
        store.addItem(seed, 3);
        playSfx(this, AUDIO.coin);
        this.ui.showToast(`🎪 ${festivalName(day)} prize! +150g & 3 seeds 💛`, 'good');
        return;
      }
    }
    this.ui.openAlmanac();
  }

  /** The owner's real-world birthday (the date set at first-run) — once that day, each year. */
  /** The shared celebration: cake, gold, confetti, Clara cheers, bunting. */
  private party(claraMsg: string, toastMsg: string, gold: number, journalMsg: string): void {
    store.update((s) => (s.money += gold));
    store.addItem('cake', 1);
    this.ui.setFestive(true);
    celebrate(this, true);
    playSfx(this, AUDIO.coin);
    this.spawnHearts(this.clara.x, this.clara.y - 12);
    this.spawnHearts(this.player.x, this.player.y - 14);
    this.showBubble(this.clara.x, this.clara.y - 14, claraMsg, 3500);
    store.logJournal(journalMsg);
    this.ui.showToast(toastMsg, 'good');
  }

  /** The first time she experiences one of her own ideas, a gentle credit. 💛 (once ever, per idea) */
  private creditIdea(flag: string, text: string): void {
    try {
      const key = `sandyhollow.idea_${flag}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      return;
    }
    this.time.delayedCall(1100, () => {
      this.ui.showToast(text, 'good');
      this.spawnHearts(this.player.x, this.player.y - 14);
    });
  }

  /** One-time fanfare the first time a v1.x build launches — a little ship party. 🎉 */
  private checkV1Launch(): void {
    try {
      if (typeof __BUILD_ID__ !== 'string' || !/^v1\./.test(__BUILD_ID__)) return;
      if (localStorage.getItem('sandyhollow.v1seen')) return;
      localStorage.setItem('sandyhollow.v1seen', '1');
    } catch {
      return;
    }
    this.time.delayedCall(1000, () => {
      celebrate(this, true);
      this.time.delayedCall(700, () => celebrate(this, true));
      store.logJournal('🎉 Reached Version 1.0!');
      this.ui.showToast('🎉 Welcome to v1.0 — thank you for playing! 💛', 'good');
    });
  }

  /** Her real-world birthday (the date set at first-run) — once that calendar day, each year. */
  private checkBirthday(): void {
    const bday = personalization.birthday;
    if (!bday) return; // no birthday set on this device
    const d = new Date();
    if (d.getMonth() + 1 !== bday.month || d.getDate() !== bday.day) return;
    const key = `bday${d.getFullYear()}`;
    if (store.get().flags[key]) return;
    store.update((s) => (s.flags[key] = true));
    const name = personalization.playerDefaultName;
    this.party('Happy Birthday! 🎉', `🎂 Happy Birthday, ${name}! +500g & a cake 💛`, 500, 'Celebrated a birthday 🎂');
  }

  /** Maps the owner's real birthday (month/day) onto an in-game day-of-year [1..48]. */
  private igBirthdayDay(): number {
    const b = personalization.birthday;
    if (!b) return 14; // a sensible default if no date was set
    return Phaser.Math.Clamp((b.month - 1) * 4 + Math.min(4, Math.ceil(b.day / 8)), 1, 48);
  }

  /** Her birthday on the IN-GAME calendar — guaranteed once per in-game year, so it always fires in play. */
  private checkInGameBirthday(): void {
    const day = store.get().clock.day;
    if (((day - 1) % 48) + 1 !== this.igBirthdayDay()) return;
    const yr = Math.floor((day - 1) / 48) + 1;
    const key = `igbday${yr}`;
    if (store.get().flags[key]) return;
    store.update((s) => (s.flags[key] = true));
    const name = personalization.playerDefaultName;
    this.party('Happy Birthday! 🎉', `🎂 Happy Birthday, ${name}! 🎉 Year ${yr} · +400g & a cake`, 400, `Celebrated ${name}'s birthday 🎂`);
  }

  /** Farm anniversary — once per completed in-game year (day 49, 97…): a lighter Year-N milestone. */
  private checkFarmAnniversary(): void {
    const day = store.get().clock.day;
    if (day <= 1 || (day - 1) % 48 !== 0) return; // a new in-game year just began
    const years = Math.floor((day - 1) / 48); // full years completed
    const key = `anniv${years}`;
    if (store.get().flags[key]) return;
    store.update((s) => {
      s.flags[key] = true;
      s.money += 250;
    });
    this.ui.setFestive(true);
    celebrate(this, true);
    playSfx(this, AUDIO.coin);
    this.spawnHearts(this.player.x, this.player.y - 14);
    store.logJournal(`The farm turned ${years} 🎉`);
    this.ui.showToast(`🎉 ${years} year${years > 1 ? 's' : ''} on the farm! +250g`, 'good');
  }

  private spawnHearts(x: number, y: number): void {
    for (let i = 0; i < 3; i++) {
      const h = this.add
        .image(x + Phaser.Math.Between(-4, 4), y, TX.heart)
        .setDepth(9500)
        .setScale(0.8);
      this.tweens.add({
        targets: h,
        y: y - 18 - i * 5,
        alpha: 0,
        duration: 650 + i * 120,
        ease: 'Sine.easeOut',
        onComplete: () => h.destroy(),
      });
    }
  }

  private showBubble(wx: number, wy: number, text: string, duration = 1800): void {
    const c = this.add.container(wx, wy - 24).setDepth(9600);
    const t = this.add
      .text(0, 0, text, { fontFamily: FONT, fontSize: '11px', color: '#2c2230', align: 'center', wordWrap: { width: 110 } })
      .setOrigin(0.5)
      .setResolution(2);
    const w = t.width + 10;
    const h = t.height + 8;
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 0.97);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 5);
    g.fillTriangle(-5, h / 2 - 1, 5, h / 2 - 1, 0, h / 2 + 6);
    c.add([g, t]);
    this.tweens.add({ targets: c, y: c.y - 6, alpha: 0, delay: duration, duration: 400, onComplete: () => c.destroy() });
  }

  // ---- ambient life -----------------------------------------------------------

  private setupAmbient(): void {
    const wpx = this.map.widthInPixels;
    const hpx = this.map.heightInPixels;

    // drifting clouds
    for (let i = 0; i < 4; i++) {
      const cloud = this.add.image(0, 0, TX.cloud).setDepth(8000).setAlpha(0.5);
      const drift = (): void => {
        cloud.setPosition(-50, Phaser.Math.Between(8, Math.floor(hpx * 0.5))).setScale(Phaser.Math.FloatBetween(1.3, 2.4));
        this.tweens.add({ targets: cloud, x: wpx + 50, duration: Phaser.Math.Between(28000, 48000), onComplete: drift });
      };
      this.time.delayedCall(i * 7000, drift);
    }

    // soft god-ray light shafts (alpha modulated by time-of-day + season)
    this.rays = [];
    for (let i = 0; i < 3; i++) {
      this.rays.push(
        this.add
          .image(wpx * (0.28 + i * 0.26), 30, TX.ray)
          .setDepth(7800)
          .setOrigin(0.5, 0)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAngle(22)
          .setScale(1.5, 2.2)
          .setAlpha(0),
      );
    }

    this.fireflies = [];
    const hpxA = this.map.heightInPixels;
    for (let i = 0; i < 7; i++) {
      const f = this.add
        .image(Phaser.Math.Between(0, wpx), Phaser.Math.Between(TS * 2, hpxA - TS * 2), TX.firefly)
        .setDepth(8300)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setScale(1.2)
        .setAlpha(0);
      this.fireflies.push(f);
      const drift = (): void => {
        if (!f.active) return;
        this.tweens.add({
          targets: f,
          x: Phaser.Math.Clamp(f.x + Phaser.Math.Between(-30, 30), 0, wpx),
          y: Phaser.Math.Clamp(f.y + Phaser.Math.Between(-24, 24), TS, hpxA - TS),
          duration: Phaser.Math.Between(1500, 3000),
          ease: 'Sine.easeInOut',
          onComplete: drift,
        });
      };
      drift();
    }

    this.moon = this.add.image(this.scale.width - 32, 32, TX.moon).setScrollFactor(0).setDepth(9100).setScale(1.3).setAlpha(0);

    this.time.addEvent({ delay: 13000, loop: true, callback: () => this.spawnBird() });
    this.time.addEvent({ delay: 5500, loop: true, callback: () => this.rollPetMode() });
    this.time.addEvent({ delay: 18000, loop: true, callback: () => { if (Math.random() < 0.4) this.petTrick(); } });
    this.time.delayedCall(6000, () => this.rollClaraCraving());
    this.time.addEvent({ delay: 240000, loop: true, callback: () => this.rollClaraCraving() }); // a fresh craving every ~4 min
    this.time.addEvent({
      delay: 26000,
      loop: true,
      callback: () => {
        if (this.claraCraving && !this.claraTarget && Math.random() < 0.45) {
          this.showBubble(this.clara.x, this.clara.y - 14, `${getItem(this.claraCraving)?.name ?? 'treat'}? 🥺`, 2000);
        }
      },
    });
    this.time.addEvent({ delay: 9000, loop: true, callback: () => this.fishSplash() });
    this.time.addEvent({ delay: 1700, loop: true, callback: () => this.chimneySmoke() });
    this.time.addEvent({
      delay: 8000,
      loop: true,
      callback: () => {
        if (Math.random() < 0.3) playSfx(this, Math.random() < 0.5 ? AUDIO.baby : AUDIO.babble);
      },
    });
    this.time.addEvent({
      delay: 11000,
      loop: true,
      callback: () => {
        if (Math.random() >= 0.35) return;
        const animals = store.get().animals;
        const sounds: AudioKey[] = [];
        if (animals.some((a) => a.type === 'cow')) sounds.push(AUDIO.moo);
        if (animals.some((a) => a.type === 'sheep')) sounds.push(AUDIO.baa);
        if (sounds.length) playSfx(this, sounds[Math.floor(Math.random() * sounds.length)]!);
      },
    });
    this.time.addEvent({
      delay: 9000,
      loop: true,
      callback: () => {
        if (Math.random() < 0.3) playSfx(this, AUDIO.chirp);
      },
    });
    this.time.addEvent({ delay: 16000, loop: true, callback: () => this.spawnBug() });
    this.time.addEvent({ delay: 14000, loop: true, callback: () => this.spawnShootingStar() });
    this.time.addEvent({
      delay: 4000,
      loop: true,
      callback: () => {
        if (minutesSinceDayStart(store.get().clock) > 780 && Math.random() < 0.4) playSfx(this, AUDIO.cricket);
      },
    });
  }

  /** An occasional fluttering bug you can tap to catch. */
  private spawnBug(): void {
    if (Math.random() > 0.5) return;
    const wpx = this.map.widthInPixels;
    const hpx = this.map.heightInPixels;
    const bug = this.add
      .image(Phaser.Math.Between(TS * 2, wpx - TS * 2), Phaser.Math.Between(TS * 3, hpx - TS * 4), TX.butterfly)
      .setDepth(8100)
      .setScale(1.2)
      .setInteractive({ useHandCursor: true });
    const flutter = (): void => {
      if (!bug.active) return;
      this.tweens.add({
        targets: bug,
        x: Phaser.Math.Clamp(bug.x + Phaser.Math.Between(-40, 40), TS, wpx - TS),
        y: Phaser.Math.Clamp(bug.y + Phaser.Math.Between(-30, 30), TS * 2, hpx - TS * 2),
        duration: Phaser.Math.Between(700, 1300),
        ease: 'Sine.easeInOut',
        onComplete: flutter,
      });
    };
    flutter();
    bug.on('pointerdown', () => {
      if (!bug.active) return;
      const kind = Math.random() < 0.6 ? 'butterfly' : 'beetle';
      store.addItem(kind, 1);
      playSfx(this, AUDIO.pop);
      this.spawnHearts(bug.x, bug.y - 8);
      this.ui.showToast(`Caught a ${getItem(kind)?.name ?? kind}!`, 'good');
      this.tweens.killTweensOf(bug);
      bug.destroy();
    });
    this.time.delayedCall(8000, () => {
      if (bug.active) {
        this.tweens.killTweensOf(bug);
        bug.destroy();
      }
    });
  }

  /** A wish-granting shooting star, only in the evening/night. */
  private spawnShootingStar(): void {
    if (minutesSinceDayStart(store.get().clock) < 780 || Math.random() > 0.45) return;
    const wpx = this.map.widthInPixels;
    const sx = Phaser.Math.Between(Math.floor(wpx * 0.4), wpx);
    const sy = Phaser.Math.Between(8, 64);
    const star = this.add
      .image(sx, sy, TX.shootingstar)
      .setDepth(8400)
      .setScale(1.4)
      .setAngle(20)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setInteractive({ useHandCursor: true });
    playSfx(this, AUDIO.twinkle);
    this.tweens.add({
      targets: star,
      x: sx - Phaser.Math.Between(130, 230),
      y: sy + Phaser.Math.Between(70, 130),
      alpha: 0,
      duration: 1500,
      ease: 'Sine.easeIn',
      onComplete: () => star.destroy(),
    });
    star.on('pointerdown', () => {
      if (!star.active) return;
      const reward = Phaser.Math.Between(3, 10) * 10;
      store.update((s) => (s.money += reward));
      playSfx(this, AUDIO.coin);
      this.spawnHearts(star.x, star.y);
      this.ui.showToast(`✨ Make a wish! +${reward}g`, 'good');
      this.tweens.killTweensOf(star);
      star.destroy();
    });
  }

  private spawnBird(): void {
    if (Math.random() > 0.6) return;
    const wpx = this.map.widthInPixels;
    const hpx = this.map.heightInPixels;
    const y = Phaser.Math.Between(8, Math.floor(hpx * 0.35));
    const dir = Math.random() < 0.5 ? 1 : -1;
    const bird = this.add
      .sprite(dir > 0 ? -20 : wpx + 20, y, TX.bird, 0)
      .setDepth(8200)
      .setFlipX(dir < 0)
      .setScale(1.2);
    if (this.anims.exists('bird-fly')) bird.play('bird-fly');
    playSfx(this, AUDIO.chirp);
    this.time.delayedCall(Phaser.Math.Between(1400, 3200), () => playSfx(this, AUDIO.chirp));
    // the pet sometimes gives chase
    if (!this.petCircling && Math.random() < 0.35) {
      this.petChase = bird;
      this.petMode = 'chase';
      this.petSound();
    }
    this.tweens.add({
      targets: bird,
      x: dir > 0 ? wpx + 20 : -20,
      y: y + Phaser.Math.Between(-16, 16),
      duration: Phaser.Math.Between(5500, 8500),
      onComplete: () => {
        if (this.petChase === bird) {
          this.petChase = null;
          this.petMode = 'follow';
        }
        bird.destroy();
      },
    });
  }

  private fishSplash(): void {
    if (this.waterTiles.length === 0 || Math.random() > 0.6) return;
    const t = this.waterTiles[Phaser.Math.Between(0, this.waterTiles.length - 1)]!;
    const ring = this.add
      .image(t.x * TS + TS / 2, t.y * TS + TS / 2, TX.moveMarker)
      .setDepth(t.y * TS + 5)
      .setTint(0x9fd0e6)
      .setScale(0.35)
      .setAlpha(0.9);
    this.tweens.add({ targets: ring, scale: 1.1, alpha: 0, duration: 650, onComplete: () => ring.destroy() });
    playSfx(this, AUDIO.splash);
  }

  private chimneySmoke(): void {
    const hp = this.bPos('house');
    const x = hp.x * TS + 36 + Phaser.Math.Between(-1, 1);
    const y = hp.y * TS + 6;
    const puff = this.add.image(x, y, TX.smoke).setDepth(9100).setAlpha(0.8).setScale(0.6);
    this.tweens.add({
      targets: puff,
      y: y - 22,
      x: x + Phaser.Math.Between(-4, 6),
      scale: 1.4,
      alpha: 0,
      duration: 2600,
      ease: 'Sine.easeOut',
      onComplete: () => puff.destroy(),
    });
  }

  // ---- fishing ----------------------------------------------------------------

  private waterAt(t: TileXY): boolean {
    return this.waterTiles.some((w) => w.x === t.x && w.y === t.y);
  }

  private cast(water: TileXY): void {
    if (this.fishing) return;
    if (!this.trySpend('fish')) return;
    const bx = water.x * TS + TS / 2;
    const by = water.y * TS + TS / 2;
    const bobber = this.add.image(bx, by, TX.bobber).setDepth(by + 20);
    this.tweens.add({ targets: bobber, y: by - 2, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    this.fishing = { bobber, water, biting: false };
    playSfx(this, AUDIO.water);
    this.ui.showToast('Fishing… wait for a bite!', 'info');
    this.fishing.biteEvent = this.time.delayedCall(Phaser.Math.Between(1600, 3600), () => this.onBite());
  }

  private onBite(): void {
    if (!this.fishing) return;
    this.fishing.biting = true;
    playSfx(this, AUDIO.pop);
    this.showBubble(this.fishing.water.x * TS + TS / 2, this.fishing.water.y * TS, '!', 1400);
    this.tweens.add({ targets: this.fishing.bobber, x: this.fishing.bobber.x + 2, duration: 70, yoyo: true, repeat: 10 });
    this.ui.showToast('Tap — it bit!', 'good');
    this.fishing.missEvent = this.time.delayedCall(2200, () => this.endFishing(false));
  }

  private fishingTap(): void {
    if (!this.fishing) return;
    this.endFishing(this.fishing.biting);
  }

  private endFishing(caught: boolean): void {
    if (!this.fishing) return;
    this.fishing.biteEvent?.remove();
    this.fishing.missEvent?.remove();
    this.tweens.killTweensOf(this.fishing.bobber);
    const water = this.fishing.water;
    this.fishing.bobber.destroy();
    this.fishing = null;
    if (caught) {
      const species = this.rollFish();
      store.addItem(species, 1);
      playSfx(this, AUDIO.harvest);
      this.fishSplashAt(water);
      if (species === 'fish_trout') {
        this.cameras.main.shake(180, 0.005);
        this.burst(water.x * TS + TS / 2, water.y * TS + TS / 2, TX.sparkle, 10);
      }
      store.logJournal('Caught my first fish 🎣');
      if (Math.random() < 0.4) this.petCirclePlayer(); // the pet sometimes celebrates with you
      this.ui.showToast(`+1 ${getItem(species)?.name ?? 'Fish'}!`, 'good');
    } else {
      this.ui.showToast('It got away…', 'info');
    }
  }

  /** Weighted random fish species — most are common, trout is the rare prize. */
  private rollFish(): string {
    const r = Math.random();
    if (r < 0.12) return 'fish_trout'; // rare prize
    if (r < 0.34) return 'fish_bass';
    if (r < 0.62) return 'fish_carp';
    if (r < 0.84) return 'fish_perch';
    return 'fish';
  }

  private fishSplashAt(t: TileXY): void {
    const ring = this.add
      .image(t.x * TS + TS / 2, t.y * TS + TS / 2, TX.moveMarker)
      .setDepth(t.y * TS + 10)
      .setTint(0x9fd0e6)
      .setScale(0.4)
      .setAlpha(0.9);
    this.tweens.add({ targets: ring, scale: 1.2, alpha: 0, duration: 600, onComplete: () => ring.destroy() });
  }

  // ---- house interior ---------------------------------------------------------

  private enterInterior(): void {
    this.ui.clearSelection();
    this.exitPlacement();
    this.scene.launch('Interior');
    this.scene.pause();
  }

  private enterForest(): void {
    this.ui.clearSelection();
    this.exitPlacement();
    this.scene.launch('Forest');
    this.scene.pause();
  }

  // ---- Move tool: relocate buildings (+ decorations, in useTool) ---------------

  private buildingAt(tile: TileXY): string | null {
    for (const b of this.BUILDINGS) {
      const p = this.bPos(b.id);
      if (tile.x >= p.x && tile.x < p.x + b.w && tile.y >= p.y && tile.y < p.y + b.h) return b.id;
    }
    return null;
  }

  private footprintClear(id: string, x: number, y: number, w: number, h: number): boolean {
    const pt = this.tileOf(this.player);
    for (let dx = 0; dx < w; dx++) {
      for (let dy = 0; dy < h; dy++) {
        const tx = x + dx;
        const ty = y + dy;
        if (tx < 0 || ty < 0 || tx >= MAP_WIDTH || ty >= MAP_HEIGHT) return false;
        if (this.mapBlocked.has(tileKey(tx, ty))) return false; // map trees/water/fences
        if (this.waterAt({ x: tx, y: ty })) return false;
        if (tx === pt.x && ty === pt.y) return false; // don't trap the player
        const other = this.buildingAt({ x: tx, y: ty });
        if (other && other !== id) return false;
      }
    }
    return true;
  }

  private tryMoveBuilding(id: string, dest: TileXY): void {
    const b = this.bDef(id);
    const x = Phaser.Math.Clamp(dest.x, 0, MAP_WIDTH - b.w);
    const y = Phaser.Math.Clamp(dest.y, 0, MAP_HEIGHT - b.h);
    if (!this.footprintClear(id, x, y, b.w, b.h)) {
      this.ui.showToast("Can't put it there", 'warn');
      return;
    }
    store.update((s) => (s.buildings[id] = { x, y }));
    if (id === 'house') this.positionHouse();
    else this.buildingSprites.get(id)?.setPosition(x * TS, y * TS).setDepth((y + b.h) * TS);
    if (id === 'mailbox') this.mailFlag?.setPosition(x * TS + 12, y * TS + 2).setDepth((y + 2) * TS);
    this.rebuildBlocked();
    this.movingBuilding = null;
    playSfx(this, AUDIO.pop);
    this.ui.showToast(`Moved the ${id}!`, 'good');
  }

  /** Render the house at its current tier, bottom-anchored so grander tiers tower upward. */
  private positionHouse(): void {
    const p = this.bPos('house');
    const tier = HOUSE_TIERS[store.get().houseLevel] ?? HOUSE_TIERS[0]!;
    this.buildingSprites
      .get('house')
      ?.setTexture(tier.texture)
      .setOrigin(0, 1)
      .setPosition(p.x * TS, p.y * TS + HOUSE_TIERS[0]!.texH)
      .setDepth((p.y + this.bDef('house').h) * TS);
  }

  /** Apply the next house upgrade if affordable. Returns false if maxed or short. */
  upgradeHouse(): boolean {
    const level = store.get().houseLevel;
    const next = HOUSE_TIERS[level + 1];
    if (!next || store.get().money < next.cost.gold) return false;
    for (const it of next.cost.items ?? []) if (store.countItem(it.itemId) < it.qty) return false;
    store.update((s) => {
      s.money -= next.cost.gold;
      s.houseLevel = level + 1;
    });
    for (const it of next.cost.items ?? []) store.removeItem(it.itemId, it.qty);
    this.positionHouse();
    celebrate(this);
    playSfx(this, AUDIO.coin);
    this.spawnHearts(this.player.x, this.player.y - 14);
    store.logJournal(`Upgraded to a ${next.name} 🏠`);
    this.ui.showToast(`🏠 Your home is now a ${next.name}!`, 'good');
    this.creditIdea('housegrows', `✨ The growing house was ${personalization.playerDefaultName}'s very own idea 💛`);
    return true;
  }

  /** Called by InteriorScene when you sleep in the house bed. */
  sleepFromInterior(): void {
    this.doSleep(false);
  }

  // ---- sleep / day rollover ---------------------------------------------------

  private doSleep(forced: boolean): void {
    if (this.sleeping) return;
    this.sleeping = true;
    this.path = [];
    this.pendingArrival = null;
    this.exitPlacement();

    this.cameras.main.fadeOut(450, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      const { state, summary } = runDayRollover(store.get(), forced);
      store.replace(state);
      saveGame(state); // autosave on day end

      // wake near the bed
      const wake = adjacentWalkable(this.houseDoor(), this.playerTile, MAP_WIDTH, MAP_HEIGHT, this.isBlocked) ?? PLAYER_SPAWN;
      this.playerTile = { ...wake };
      this.placePlayerAtTile(wake);
      store.update((s) => (s.player = { ...wake }));
      this.player.setScale(1);
      this.tweens.add({ targets: this.player, scaleY: 1.18, scaleX: 0.92, duration: 420, yoyo: true, ease: 'Sine.easeInOut' });
      playSfx(this, AUDIO.yawn);

      this.spawnWeeds();
      this.layEggs();
      this.growGroves();
      for (const d of store.get().decorations) if (d.decoId === 'bee_hive') store.addItem('honey', 1);
      this.rollWeather();
      this.applySprinklers();
      this.rollMail();
      store.update((s) => {
        for (const k of Object.keys(s.flags)) if (k.startsWith('forage')) s.flags[k] = false;
      });
      if (store.get().petHearts >= 5 && Math.random() < 0.7) {
        const finds = ['berry', 'mushroom', 'herb', 'shell', 'apple'];
        const item = finds[Math.floor(Math.random() * finds.length)]!;
        store.addItem(item, 1);
        const pn = store.get().petName || (store.get().petType === 'dog' ? 'Your dog' : 'Your cat');
        this.ui.showToast(`${pn} brought you a ${getItem(item)?.name ?? item}! 🐾`, 'good');
      }
      if (store.get().claraHearts >= 8) {
        let helped = 0;
        store.update((s) => {
          for (const c of s.crops) {
            if (!c.watered) {
              c.watered = true;
              if (++helped >= 3) break;
            }
          }
        });
        if (helped > 0) this.ui.showToast(`Clara watered ${helped} crop${helped > 1 ? 's' : ''}! 💛`, 'good');
      }
      const fday = store.get().clock.day;
      this.ui.setFestive(isFestivalDay(fday));
      setFestival(isFestivalDay(fday));
      if (isFestivalDay(fday)) {
        this.ui.showToast(`🎪 ${festivalName(fday)} today — tap the sign!`, 'good');
        confetti(this);
      }
      this.checkBirthday();
      this.checkInGameBirthday();
      this.checkFarmAnniversary();
      if (store.get().clock.day % 7 === 0) this.ui.showToast('🌕 A full moon tonight!', 'info');
      this.renderWorldFromState();
      this.cameras.main.fadeIn(450, 0, 0, 0);
      this.sleeping = false;
      this.ui.showDaySummary(summary);
    });
  }

  // ---- update loop ------------------------------------------------------------

  update(_time: number, delta: number): void {
    if (this.sleeping) return;

    this.advanceTime(delta);
    if (this.dragArmed && this.dragMoved && this.input.activePointer.isDown) this.directMove(delta);
    else this.moveAlongPath(delta);
    this.updatePet(delta);
    this.updateClara(delta);
    this.updateAnimals(delta);
    this.updateDepthSort();
    this.updateTint();
  }

  private advanceTime(delta: number): void {
    this.minuteAcc += realMsToGameMinutes(delta);
    const whole = Math.floor(this.minuteAcc);
    if (whole > 0) {
      this.minuteAcc -= whole;
      store.update((s) => (s.clock = addGameMinutes(s.clock, whole)));
      if (isPastForceSleep(store.get().clock) && !store.toddlerMode) {
        this.ui.showToast('You collapsed from exhaustion…', 'warn');
        this.doSleep(true);
      }
    }
  }

  private moveAlongPath(delta: number): void {
    if (this.path.length === 0) return;
    const target = this.path[0]!;
    const fx = target.x * TS + TS / 2;
    const fy = target.y * TS + TS;
    const dx = fx - this.player.x;
    const dy = fy - this.player.y;
    const dist = Math.hypot(dx, dy);
    this.faceMove(dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    if (dist <= step || dist === 0) {
      this.player.setPosition(fx, fy);
      this.playerTile = { x: target.x, y: target.y };
      this.path.shift();
      if (this.path.length === 0) {
        store.update((s) => (s.player = { ...this.playerTile }));
        this.faceIdle();
        const a = this.pendingArrival;
        this.pendingArrival = null;
        a?.();
      }
    } else {
      this.player.x += (dx / dist) * step;
      this.player.y += (dy / dist) * step;
    }
  }

  private petSound(): void {
    playSfx(this, store.get().petType === 'dog' ? AUDIO.bark : AUDIO.meow);
  }

  /** The pet trots a couple of laps around you (a happy little celebration). */
  private petCirclePlayer(): void {
    if (this.petCircling) return;
    this.petCircling = true;
    this.petSound();
    const o = { a: Math.random() * Math.PI * 2 };
    this.tweens.add({
      targets: o,
      a: o.a + Math.PI * 4,
      duration: 1600,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this.pet.x = this.player.x + Math.cos(o.a) * 22;
        this.pet.y = this.player.y + Math.sin(o.a) * 13;
      },
      onComplete: () => (this.petCircling = false),
    });
  }

  private updatePet(delta: number): void {
    if (this.petCircling) return; // the circle tween is driving the pet
    if (this.petMode === 'sleep' && !this.petTarget) {
      if (Math.random() < 0.006) this.petZzz();
      return; // curled up where it stands
    }
    let tx: number;
    let ty: number;
    let gapTiles: number;
    if (this.petMode === 'chase' && this.petChase?.active) {
      tx = this.petChase.x;
      ty = this.petChase.y;
      gapTiles = 0.3;
    } else if (this.petMode === 'sleep' && this.petTarget) {
      tx = this.petTarget.x;
      ty = this.petTarget.y;
      gapTiles = 0.15;
    } else if (this.petMode === 'clara') {
      tx = this.clara.x;
      ty = this.clara.y;
      gapTiles = 1;
    } else if (this.petMode === 'wander' && this.petTarget) {
      tx = this.petTarget.x;
      ty = this.petTarget.y;
      gapTiles = 0.2;
    } else {
      tx = this.player.x;
      ty = this.player.y;
      gapTiles = CONFIG.petFollowGapTiles + 1.2; // chill a little further back so it's not underfoot
    }
    const dx = tx - this.pet.x;
    const dy = ty - this.pet.y;
    const d = Math.hypot(dx, dy);
    const gap = gapTiles * TS;
    if (d > gap) {
      const speed = CONFIG.petSpeedTilesPerSecond * (this.petMode === 'wander' ? 0.5 : this.petMode === 'chase' ? 1.7 : 1);
      const m = Math.min((speed * TS * delta) / 1000, d - gap);
      // respect tile bounds: never walk onto water/structures (slide along instead)
      const nx = this.pet.x + (dx / d) * m;
      const ny = this.pet.y + (dy / d) * m;
      const foot = (fx: number, fy: number): TileXY => ({ x: Math.floor(fx / TS), y: Math.floor((fy - 1) / TS) });
      if (!this.isBlocked(foot(nx, this.pet.y).x, foot(nx, this.pet.y).y)) this.pet.x = nx;
      if (!this.isBlocked(foot(this.pet.x, ny).x, foot(this.pet.x, ny).y)) this.pet.y = ny;
      if (Math.abs(dx) > 0.4) this.pet.setFlipX(dx < 0);
    } else if (this.petMode === 'clara' && Math.random() < 0.005) {
      this.spawnHearts((this.pet.x + this.clara.x) / 2, (this.pet.y + this.clara.y) / 2 - 8); // a sweet moment with Clara
    } else if (this.petMode === 'sleep' && Math.random() < 0.012) {
      this.petZzz();
    }
  }

  private petZzz(): void {
    const z = this.add
      .text(this.pet.x + 4, this.pet.y - 14, '💤', { fontFamily: 'monospace', fontSize: '10px' })
      .setOrigin(0.5)
      .setDepth(9000)
      .setResolution(2);
    this.tweens.add({ targets: z, y: z.y - 12, alpha: 0, duration: 1700, ease: 'Sine.easeOut', onComplete: () => z.destroy() });
  }

  /** Every few seconds the pet picks a mood: follow you (most), wander a bit, or go visit Clara. */
  private rollPetMode(): void {
    const h = store.get().clock.hour;
    if (h >= 21 || h < 6) {
      // night — curl up to sleep, heading to the pet bed if one's placed
      this.petMode = 'sleep';
      const bed = store.get().decorations.find((d) => d.decoId === 'pet_bed');
      this.petTarget = bed ? { x: bed.x * TS + TS / 2, y: bed.y * TS + TS } : null;
      return;
    }
    const r = Math.random();
    if (r < 0.55) {
      this.petMode = 'follow';
      this.petTarget = null;
    } else if (r < 0.8) {
      // wander to a nearby *walkable* tile (no strolling into the pond)
      this.petMode = 'follow';
      this.petTarget = null;
      const bx = Math.floor(this.pet.x / TS);
      const by = Math.floor(this.pet.y / TS);
      for (let i = 0; i < 6; i++) {
        const tx = Phaser.Math.Clamp(bx + Phaser.Math.Between(-3, 3), 1, MAP_WIDTH - 2);
        const ty = Phaser.Math.Clamp(by + Phaser.Math.Between(-3, 3), 1, MAP_HEIGHT - 2);
        if (!this.isBlocked(tx, ty)) {
          this.petMode = 'wander';
          this.petTarget = { x: tx * TS + TS / 2, y: ty * TS + TS };
          this.creditIdea('catwander', `✨ The wandering cat was ${personalization.playerDefaultName}'s very own idea 💛`);
          break;
        }
      }
    } else {
      this.petMode = 'clara';
      this.petTarget = null;
    }
  }

  /** A little personality trick — the cat wiggles then pounces a "mouse"; the dog digs a hole. */
  private petTrick(): void {
    if (this.petCircling || this.petMode === 'chase' || this.petMode === 'sleep') return;
    if (store.get().petType === 'dog') this.dogDig();
    else this.catPounce();
  }

  private catPounce(): void {
    this.petCircling = true; // pause normal follow; the tween drives the pet
    const x = this.pet.x;
    const y = this.pet.y;
    this.tweens.chain({
      targets: this.pet,
      tweens: [
        { scaleX: 1.18, scaleY: 0.82, duration: 220, ease: 'Quad.easeOut' },
        { x: x - 2, duration: 70, yoyo: true, repeat: 2 },
        { x: x + 14, y: y - 10, duration: 130, ease: 'Quad.easeOut', onComplete: () => { this.burst(x + 18, y, TX.dust, 5); this.petSound(); } },
        { x, y, scaleX: 1, scaleY: 1, duration: 190, ease: 'Quad.easeIn' },
      ],
      onComplete: () => (this.petCircling = false),
    });
  }

  private dogDig(): void {
    this.petCircling = true;
    const x = this.pet.x;
    const y = this.pet.y;
    this.tweens.chain({
      targets: this.pet,
      tweens: [
        { y: y - 3, duration: 120, yoyo: true, repeat: 3, onYoyo: () => this.burst(x, y + 4, TX.dust, 3) },
        { y, duration: 100 },
      ],
      onComplete: () => {
        this.petCircling = false;
        this.petSound();
        if (Math.random() < 0.3) {
          store.addItem('wood', 1);
          this.ui.showToast('Your dog dug up a stick! 🦴 +1 Wood', 'good');
        }
      },
    });
  }

  private updateClara(delta: number): void {
    if (this.claraThought) {
      this.claraThought.setPosition(this.clara.x, this.clara.y - 28 - Math.sin(this.time.now / 300) * 1.5).setDepth(this.clara.depth + 2);
    }
    if (this.claraTarget) {
      const fx = this.claraTarget.x * TS + TS / 2;
      const fy = this.claraTarget.y * TS + TS;
      const dx = fx - this.clara.x;
      const dy = fy - this.clara.y;
      const d = Math.hypot(dx, dy);
      const step = (1.6 * TS * delta) / 1000;
      if (Math.abs(dx) > 0.4) this.clara.setFlipX(dx < 0);
      if (d <= step) {
        this.clara.setPosition(fx, fy);
        this.claraTile = { ...this.claraTarget };
        this.claraTarget = null;
        this.claraWait = Phaser.Math.Between(700, 2200);
      } else {
        this.clara.x += (dx / d) * step;
        this.clara.y += (dy / d) * step;
      }
      return;
    }
    this.claraWait -= delta;
    if (this.claraWait > 0) return;
    // sometimes toddle toward the cat instead of wandering at random
    if (Math.random() < 0.3) {
      const pt = this.tileOf(this.pet);
      const near =
        Math.abs(pt.x - CLARA_HOME.x) <= CONFIG.claraWanderRadiusTiles + 2 &&
        Math.abs(pt.y - CLARA_HOME.y) <= CONFIG.claraWanderRadiusTiles + 2;
      if (near) {
        const dx = pt.x - this.claraTile.x;
        const dy = pt.y - this.claraTile.y;
        let nx = this.claraTile.x;
        let ny = this.claraTile.y;
        if (Math.abs(dx) >= Math.abs(dy) && dx !== 0) nx += Math.sign(dx);
        else if (dy !== 0) ny += Math.sign(dy);
        if ((nx !== this.claraTile.x || ny !== this.claraTile.y) && !this.isBlocked(nx, ny) && !(nx === pt.x && ny === pt.y)) {
          this.claraTarget = { x: nx, y: ny };
          return;
        }
      }
    }
    // pick a random adjacent walkable tile within wander radius of home
    const dirs = [[0, -1], [0, 1], [-1, 0], [1, 0]].sort(() => Math.random() - 0.5);
    for (const [ox, oy] of dirs) {
      const nx = this.claraTile.x + ox!;
      const ny = this.claraTile.y + oy!;
      const within =
        Math.abs(nx - CLARA_HOME.x) <= CONFIG.claraWanderRadiusTiles &&
        Math.abs(ny - CLARA_HOME.y) <= CONFIG.claraWanderRadiusTiles;
      if (within && !this.isBlocked(nx, ny)) {
        this.claraTarget = { x: nx, y: ny };
        return;
      }
    }
    this.claraWait = 1000;
  }

  private updateDepthSort(): void {
    this.player.setDepth(this.player.y);
    this.pet.setDepth(this.pet.y);
    this.clara.setDepth(this.clara.y);
    if (this.hat) {
      this.hat
        .setPosition(this.player.x, this.player.y - HAT_OFFSET_Y)
        .setFlipX(this.player.flipX)
        .setDepth(this.player.y + 1);
    }
    if (this.claraHat) {
      this.claraHat.setPosition(this.clara.x, this.clara.y - 13).setDepth(this.clara.y + 1);
    }
    if (this.petCollar) {
      this.petCollar.setPosition(this.pet.x, this.pet.y - 5).setDepth(this.pet.y + 1);
    }
  }

  /** (Re)build the pet's collar overlay from the saved color. */
  applyPetCollar(): void {
    if (this.petCollar) {
      this.petCollar.destroy();
      this.petCollar = undefined;
    }
    const col = collarColor(getCollar());
    if (col !== undefined) {
      this.petCollar = this.add.rectangle(this.pet.x, this.pet.y - 5, 8, 2, col).setDepth(this.pet.y + 1);
    }
  }

  /** (Re)build Clara's outfit overlay (settings calls it on change). */
  applyClaraOutfit(): void {
    if (this.claraHat) {
      this.claraHat.destroy();
      this.claraHat = undefined;
    }
    const tex = claraOutfitTexture(getClaraOutfit());
    if (tex) {
      this.claraHat = this.add
        .image(this.clara.x, this.clara.y - 13, tex)
        .setOrigin(0.5, 0.5)
        .setScale(0.85)
        .setDepth(this.clara.y + 1);
    }
  }

  /** (Re)build the cosmetic hat from the saved outfit choice. Public: settings calls it on change. */
  applyHat(): void {
    if (this.hat) {
      this.hat.destroy();
      this.hat = undefined;
    }
    const tex = hatTexture(getHat());
    if (tex) {
      this.hat = this.add
        .image(this.player.x, this.player.y - HAT_OFFSET_Y, tex)
        .setOrigin(0.5, 0.5)
        .setDepth(this.player.y + 1);
    }
  }

  private updateTint(): void {
    const view = this.cameras.main.worldView;
    const clock = store.get().clock;
    const mins = minutesSinceDayStart(clock);
    this.nightOverlay.setPosition(view.x, view.y).setSize(view.width, view.height);
    const { color, alpha } = ambientTint(mins);
    this.nightOverlay.setFillStyle(color, alpha);

    const season = seasonOf(clock.day);
    if (season.id !== this.lastSeasonId) {
      this.lastSeasonId = season.id;
      setSeason(season.id);
      this.ui.showSnow(season.id === 'winter');
      this.ui.showLeaves(season.id === 'fall');
    }
    this.seasonOverlay
      .setPosition(view.x, view.y)
      .setSize(view.width, view.height)
      .setFillStyle(season.tint, 0.16);

    // sun shafts: brightest at midday, scaled by season, gently pulsing
    const sun = Math.max(0, 1 - Math.abs(mins - 360) / 360) * (SEASON_SUN[season.id] ?? 0.5);
    const now = this.time.now;
    for (let i = 0; i < this.rays.length; i++) {
      this.rays[i]!.setAlpha(sun * 0.12 * (0.7 + 0.3 * Math.sin(now / 1500 + i * 2)));
    }
    // fireflies + moon appear only at real dusk/night — 0 until the overlay is genuinely dark, so none in daylight
    const night = Phaser.Math.Clamp((alpha - 0.35) / 0.3, 0, 1);
    for (let i = 0; i < this.fireflies.length; i++) {
      this.fireflies[i]!.setAlpha(night * (0.45 + 0.45 * Math.sin(now / 480 + i * 1.7)));
    }
    if (this.moon) {
      this.moon.setAlpha(night).setScale(clock.day % 7 === 0 ? 2.0 : 1.3); // bigger on a full moon
    }
  }

  // ---- debug hooks (called by DebugScene) -------------------------------------

  dbgAddMinutes(mins: number): void {
    store.update((s) => (s.clock = addGameMinutes(s.clock, mins)));
  }
  dbgSleep(): void {
    this.doSleep(false);
  }
  dbgRefillEnergy(): void {
    store.update((s) => (s.energy = s.energyMax));
  }
  dbgGiveItem(itemId: string, qty: number): void {
    store.addItem(itemId, qty);
  }
  dbgAddMoney(amount: number): void {
    store.update((s) => (s.money += amount));
  }
  dbgTeleportMode(): void {
    this.teleportMode = true;
    this.ui.showToast('Tap to teleport', 'info');
  }
}

/** Ambient day/night tint by minutes since the 6am day start (0–1200). */
function ambientTint(mins: number): { color: number; alpha: number } {
  // 0=6am .. 600=4pm .. 840=8pm .. 1200=2am
  const stops: Array<{ at: number; color: number; alpha: number }> = [
    { at: 0, color: 0xffe6c0, alpha: 0.1 }, // dawn
    { at: 150, color: 0xffffff, alpha: 0.0 }, // morning
    { at: 580, color: 0xffffff, alpha: 0.0 }, // afternoon
    { at: 720, color: 0xffb066, alpha: 0.16 }, // sunset
    { at: 860, color: 0x3a4a82, alpha: 0.46 }, // dusk
    { at: 1000, color: 0x1a2444, alpha: 0.66 }, // night
    { at: 1200, color: 0x10182e, alpha: 0.74 }, // deep night
  ];
  let a = stops[0]!;
  let b = stops[stops.length - 1]!;
  for (let i = 0; i < stops.length - 1; i++) {
    if (mins >= stops[i]!.at && mins <= stops[i + 1]!.at) {
      a = stops[i]!;
      b = stops[i + 1]!;
      break;
    }
  }
  const span = b.at - a.at || 1;
  const t = Phaser.Math.Clamp((mins - a.at) / span, 0, 1);
  const ca = Phaser.Display.Color.IntegerToColor(a.color);
  const cb = Phaser.Display.Color.IntegerToColor(b.color);
  const col = Phaser.Display.Color.Interpolate.ColorWithColor(ca, cb, 100, t * 100);
  const color = Phaser.Display.Color.GetColor(col.r, col.g, col.b);
  return { color, alpha: a.alpha + (b.alpha - a.alpha) * t };
}
